import React, {
  KeyboardEventHandler,
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import { CircularProgress } from '@mui/material';
import { ChevronDown, ChevronRight, Code, Globe, Square } from 'react-feather';
import ReplayIcon from '@mui/icons-material/Replay';
import type {
  ApiError,
  InputChangeEvent,
  StreamError,
  Agent,
  McpServer,
  PiiScan,
} from '../../../types/api-types';
import RenderMcpExpandedContent from '../../../components/render-mcp-expanded-content';
import { invalidateMcpWidgetHtmlCache } from '../../../components/mcp-widget-container';
import {
  buildFileUploadMessageText,
  isFileUploadHumanMessage,
} from '../../../lib/file-upload-utils';
import {
  effectiveAgentToolEnabled,
  effectiveAgentWebSearchMode,
  mergeWebSearchSources,
} from '../../../lib/chat-tooling';
import { isAutoRoutingFeatureEnabled, loadRoutingMode } from '../../../lib/routing-mode';
import {
  assignedFeatureAvailability,
  hasActiveFeatureRequirements,
  pickCompatibleModel,
  type ChatFeatureContext,
} from '../../../lib/model-capability-filter';
import {
  applyPickedChatModel,
  persistWebSearchFlags,
  resolveWebSearchMode,
  type WebSearchMode,
} from '../../../lib/chat-model-persist';
import {
  appendMcpWarning,
  finalizeToolCalls,
  formatMcpUserNotice,
  formatMcpSidePanelFailure,
  getToolCallErrorText,
  isMcpConnectivityError,
  isMcpConnectivityMessage,
  mcpCanUserToggle,
  mcpShowsReconnectActions,
  patchToolCallResult,
  resolveMcpIdFromNotice,
} from '../../../lib/mcp-tool-utils';
import type { StreamEvent } from '../../../services/chat';
import TuneModal from '../../../components/tune-modal';
import {
  EMPTY_MCP_RE_AUTH,
  getMcpAuthTypeLabel,
  getMcpStatusBadge,
  type McpReAuthContext,
} from '../../../components/tune-modal/tune-modal.props';
import { ActionModal, MakeToast } from '../../../../../../components';
import {
  CreateNewChatSession,
  deleteAgent,
  DeleteMcpChat,
  EditMcpChatServce,
  generateNewChat2,
  getAgentById,
  getAllAgents,
  GetAllMcpChatService,
  RefreshMcpToolsService,
  terminateChatStream,
  UpdateModelForProvider,
  uploadFile,
} from '../../../services/chat';
import type {
  PendingFileAction,
  FileUploadPendingAction,
} from '../../../../../../utils/reducer-types';
import { ChatDataItem, queryContainerProps } from '../chat-screen.props';
import {
  hasMessageText,
  sanitizeChatHistoryForHydration,
  type DataItem,
  type ToolCall,
} from '../../../../../../utils/interface-types';
import {
  DeleteIcon,
  AttachIcon,
  SendButtonIcon,
  ChatAgentIcon,
  EditIcon,
  CrossIcon,
  BrainIcon,
  TextFile,
} from '../../../../../../assets/images';
import Logout from '../../../../../../auth/protected/session-expire-modal';
import StyledTooltip from '../../../../../../components/tooltip';
import './query-container.css';
import ChatComposerShell from '../../../components/chat-ui/chat-composer-shell';
import ChatThreadLayout from '../../../components/chat-ui/chat-thread-layout';
import ModelTabs from '../../../components/models-tabs';
import makeToast from '../../../../../../components/toaster';
import ChatAgentModal from '../../../components/chat-agent-modal';
import PiiUploadConfirmationModal from '../../../components/pii-upload-confirmation-modal';
import { UseDocument } from '../../../components/data/constants';
import { ChatModelContext, LlmSetupBannerContext } from '../../../../../../utils/context';
import {
  ADMIN_DISABLED_MODEL_MESSAGE,
  isAdminBlockedModelStatus,
} from '../../../../../../services';

const WEB_SEARCH_MODE_LABELS: Record<WebSearchMode, string> = {
  auto: 'Auto',
  on: 'On',
  off: 'Off',
};

type McpStatusBadgeProps = {
  status?: string;
};

function McpStatusBadge({ status }: McpStatusBadgeProps) {
  const { className, label } = getMcpStatusBadge(status);
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${className}`}>{label}</span>;
}

McpStatusBadge.defaultProps = {
  status: undefined,
};

function QueryContainer({
  state,
  dispatch,
  setIsContentAvailable,
  sendButtonRef,
  setShowError,
  activeProvider,
  onClose,
  llmPermissions,
  llmPermissionsLoaded = false,
  setActiveProvider,
  streamControlRef,
  onGenerationUiChange,
  isGenerationActive = false,
  onStopGeneration,
}: queryContainerProps) {
  const { showLlmSetupBanner } = useContext(LlmSetupBannerContext);
  const { setChatModel } = useContext(ChatModelContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tunePopupRef = useRef<HTMLDivElement>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  // Skip DOM rewrite in the queryValue sync effect when the change came from typing
  const skipContentEditableSyncRef = useRef(false);
  const [logout, setLogout] = useState<boolean>(false);
  const [showTunePopup, setShowTunePopup] = useState<boolean>(false);
  const [showTuneModal, setShowTuneModal] = useState<boolean>(false);
  const [mcpData, setMcpData] = useState<McpServer[]>([]);
  const [expandedMcp, setExpandedMcp] = useState<number | null>(null);
  const [mcpPanelMessages, setMcpPanelMessages] = useState<Record<number, string>>({});
  const [mcpRefreshingId, setMcpRefreshingId] = useState<number | null>(null);
  const [reAuthOpenModa, setReAuthOpenModa] = useState<McpReAuthContext>(EMPTY_MCP_RE_AUTH);
  const [mcpPrefillConfig, setMcpPrefillConfig] = useState<McpReAuthContext | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [showChatAgentModal, setShowChatAgentModal] = useState<boolean>(false);
  const [expandedAgents, setExpandedAgents] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    agentId: number | null;
    agentName: string;
  }>({ show: false, agentId: null, agentName: '' });
  const [deleteMcpConfirmation, setDeleteMcpConfirmation] = useState<{
    show: boolean;
    mcpId: number | null;
    mcpName: string;
  }>({ show: false, mcpId: null, mcpName: '' });
  const [piiConfirmation, setPiiConfirmation] = useState<{
    show: boolean;
    piiScan: PiiScan | null;
  }>({ show: false, piiScan: null });
  const [pendingUploadedFileId, setPendingUploadedFileId] = useState<string | null>(null);
  const piiUploadContextRef = useRef<{
    sessionIdToUse: string;
    file: File;
    fileId: string;
  } | null>(null);
  const fileUploadRequestIdRef = useRef(0);
  const pendingUploadSessionIdRef = useRef<string | null>(null);
  const fileUploadAbortControllerRef = useRef<AbortController | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [showAgentMention, setShowAgentMention] = useState<boolean>(false);
  const [mentionQuery, setMentionQuery] = useState<string>('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number>(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState<number>(0);
  const [selectedQueryAgent, setSelectedQueryAgent] = useState<Agent | null>(null);
  const agentMentionRef = useRef<HTMLDivElement>(null);
  const previousChatHistoryLengthRef = useRef<number>(0);
  const previousDataChangedRef = useRef<boolean>(false);
  const previousActiveProviderRef = useRef<number | null>(activeProvider);
  const isInitialMountRef = useRef<boolean>(true);
  const streamAbortControllerRef = useRef<AbortController | null>(null);
  const streamStoppedRef = useRef(false);
  const streamStopRequestedRef = useRef(false);
  const stopFinalizeTimeoutRef = useRef<number | null>(null);
  const activeStreamFinalizeRef = useRef<{
    chat: DataItem[];
    aiMessageIndex: number;
    persistChat: (chat: DataItem[]) => void;
    streamSessionId: number;
    tokenUsage?: {
      input_tokens?: number;
      reasoning_tokens?: number;
      output_tokens?: number;
    };
  } | null>(null);
  const streamSessionIdRef = useRef(0);
  const retryRunnerRef = useRef<(() => void) | null>(null);
  const handleSendQueryRef = useRef<(() => void) | null>(null);
  const pendingRetryQueryRef = useRef<string | null>(null);
  const retryContextRef = useRef<{ sessionId: string; queryText: string } | null>(null);
  const retryBaseChatRef = useRef<DataItem[] | null>(null);
  const retryAiMessageIndexRef = useRef<number | null>(null);
  const skipAppendAiOnRetryRef = useRef(false);
  const retryPreparedChatRef = useRef<DataItem[] | null>(null);
  const skipAddHumanOnNextSendRef = useRef(false);

  const activeQueryAgent =
    selectedQueryAgent?.provider_id === activeProvider ? selectedQueryAgent : null;

  const routingModeForSend = (): 'auto' | 'manual' => {
    if (!isAutoRoutingFeatureEnabled() || activeQueryAgent?.id) {
      return 'manual';
    }
    return loadRoutingMode();
  };

  const allMcpApi = async (): Promise<McpServer[]> => {
    try {
      const res = await GetAllMcpChatService();
      if (res.status === 200) {
        const mcpResponse = res?.data?.data || [];
        setMcpData(mcpResponse);
        return mcpResponse;
      }
      if (res.status === 401) {
        setLogout(true);
      }
    } catch (error) {
      MakeToast('error', 'Failed to fetch MCP configurations');
    }
    return [];
  };

  const openMcpConnectModal = (mcp: McpServer) => {
    setReAuthOpenModa(EMPTY_MCP_RE_AUTH);
    setMcpPrefillConfig({
      url: mcp.url,
      mcpName: mcp.name,
      authType: mcp.auth_type,
      transportType: mcp.transportType,
      mcpId: mcp.id,
    });
    setShowTunePopup(false);
    setShowTuneModal(true);
  };

  const clearMcpPanelMessage = (mcpId: number) => {
    setMcpPanelMessages((prev) => {
      if (!(mcpId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[mcpId];
      return next;
    });
  };

  const showMcpFailureInSidePanel = async ({
    message,
    mcpId,
  }: {
    message: string;
    mcpId?: number;
  }) => {
    const formatted = formatMcpSidePanelFailure(message);
    const mcps = await allMcpApi();
    const resolvedId = mcpId ?? resolveMcpIdFromNotice(message, mcps);

    if (resolvedId != null) {
      setMcpPanelMessages((prev) => ({ ...prev, [resolvedId as number]: formatted }));
      setExpandedMcp(resolvedId);
    } else {
      setExpandedMcp((prev) => (prev === null ? 0 : prev));
    }
  };

  const handleMcpStreamNotice = (message: string) => {
    if (isMcpConnectivityMessage(message)) {
      showMcpFailureInSidePanel({ message }).catch(() => undefined);
      return;
    }
    MakeToast('info', formatMcpUserNotice(message), 6000);
    allMcpApi();
  };

  const handleMcpConnectionFailed = async (failure?: { message?: string; mcp_id?: number }) => {
    if (failure?.message) {
      await showMcpFailureInSidePanel({
        message: failure.message,
        mcpId: failure.mcp_id,
      });
      return;
    }
    await allMcpApi();
  };

  const handleMcpToolResultFailure = async (resultEvent: {
    tool_name?: string;
    mcp_id?: number;
    error?: unknown;
  }) => {
    if (!resultEvent.error || !isMcpConnectivityError(resultEvent.error)) {
      return;
    }
    const errorText =
      typeof resultEvent.error === 'string'
        ? resultEvent.error
        : getToolCallErrorText(resultEvent.error);
    await showMcpFailureInSidePanel({
      message: errorText,
      mcpId: resultEvent.mcp_id,
    });
  };

  const fetchAgents = async () => {
    try {
      const res = await getAllAgents();
      if (res.status === 200) {
        const d = res?.data?.data;
        const agentsData = Array.isArray(d)
          ? d
          : ((d as { agents?: Agent[] } | undefined)?.agents ?? []);
        setAgents(agentsData);
      } else if (res.status === 401) {
        setLogout(true);
      }
    } catch (error) {
      MakeToast('error', 'Failed to fetch agents');
    }
  };

  useEffect(() => {
    fetchAgents();
    allMcpApi();
  }, [showTuneModal]);

  // Initialize the refs on mount
  useEffect(() => {
    previousChatHistoryLengthRef.current = state.chatHistory.length;
    previousDataChangedRef.current = state.dataChanged ?? false;
    // Mark that initial mount is complete after first render
    setTimeout(() => {
      isInitialMountRef.current = false;
    }, 0);
  }, []); // Only run once on mount

  // Clear selected agent when new chat is started - watch chat history length
  useEffect(() => {
    // Skip on initial mount to avoid clearing agent on page load
    if (isInitialMountRef.current) {
      previousChatHistoryLengthRef.current = state.chatHistory.length;
      return;
    }

    const currentLength = state.chatHistory.length;
    const previousLength = previousChatHistoryLengthRef.current;

    // Clear agent when chat history transitions from non-empty to empty
    // This happens when "New Chat" is clicked from header or chat history
    if (previousLength > 0 && currentLength === 0) {
      if (selectedAgent !== null || selectedQueryAgent !== null) {
        setSelectedAgent(null);
        setSelectedQueryAgent(null);
      }
      fileUploadRequestIdRef.current += 1;
      fileUploadAbortControllerRef.current?.abort();
      fileUploadAbortControllerRef.current = null;
      dispatch({ type: 'SET_FILE_UPLOAD_PENDING', payload: false } as FileUploadPendingAction);
      setPendingUploadedFileId(null);
      pendingUploadSessionIdRef.current = null;
      dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
      setPiiConfirmation({ show: false, piiScan: null });
      piiUploadContextRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    // Update the ref for next comparison
    previousChatHistoryLengthRef.current = currentLength;
  }, [state.chatHistory.length, selectedAgent, selectedQueryAgent, dispatch]);

  // Also watch for dataChanged toggle as an additional signal when "New Chat" is clicked
  useEffect(() => {
    if (isInitialMountRef.current) {
      previousDataChangedRef.current = state.dataChanged ?? false;
      return;
    }

    const currentDataChanged = state.dataChanged ?? false;
    const previousDataChanged = previousDataChangedRef.current;
    const currentLength = state.chatHistory.length;

    // Clear agent when dataChanged toggles AND chat history is empty
    // This catches cases where "New Chat" is clicked
    if (
      currentDataChanged !== previousDataChanged &&
      currentLength === 0 &&
      (selectedAgent !== null || selectedQueryAgent !== null)
    ) {
      setSelectedAgent(null);
      setSelectedQueryAgent(null);
    }

    // Update the ref for next comparison
    previousDataChangedRef.current = currentDataChanged;
  }, [state.dataChanged, state.chatHistory.length, selectedAgent, selectedQueryAgent]);

  // Clear selected chat agents + pending file on provider switch (session-scoped)
  useEffect(() => {
    if (isInitialMountRef.current) {
      previousActiveProviderRef.current = activeProvider;
      return;
    }

    const previousProvider = previousActiveProviderRef.current;
    if (
      previousProvider !== null &&
      activeProvider !== null &&
      previousProvider !== activeProvider
    ) {
      if (selectedAgent !== null || selectedQueryAgent !== null) {
        setSelectedAgent(null);
        setSelectedQueryAgent(null);
      }
      fileUploadRequestIdRef.current += 1;
      fileUploadAbortControllerRef.current?.abort();
      fileUploadAbortControllerRef.current = null;
      dispatch({ type: 'SET_FILE_UPLOAD_PENDING', payload: false } as FileUploadPendingAction);
      setPendingUploadedFileId(null);
      pendingUploadSessionIdRef.current = null;
      dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
      setPiiConfirmation({ show: false, piiScan: null });
      piiUploadContextRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    if (activeProvider !== null) {
      previousActiveProviderRef.current = activeProvider;
    }
  }, [activeProvider, selectedAgent, selectedQueryAgent, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tunePopupRef.current && !tunePopupRef.current.contains(event.target as Node)) {
        setShowTunePopup(false);
      }
      if (
        agentMentionRef.current &&
        !agentMentionRef.current.contains(event.target as Node) &&
        contentEditableRef?.current &&
        !contentEditableRef.current.contains(event.target as Node)
      ) {
        setShowAgentMention(false);
        setMentionQuery('');
        setMentionStartIndex(-1);
      }
    };
    if (showTunePopup || showAgentMention) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTunePopup, showAgentMention]);

  const ragConfig = localStorage.getItem('configurations');
  let LLMs = [];
  if (ragConfig) {
    LLMs = JSON.parse(ragConfig)?.llm;
  }
  const RagOptions = LLMs?.map((item: string) => {
    return {
      label: item,
      value: item,
    };
  });

  const permissionsJson = localStorage.getItem('permissions');
  let permittedFile: { data: string[]; module: string }[] = [];
  if (permissionsJson !== null) {
    const parsedPermissions: { data: Array<string>; module: string }[] =
      JSON.parse(permissionsJson);
    permittedFile = parsedPermissions.filter(
      (permission) => permission.module === 'chat' && permission.data?.includes('file-upload'),
    );
  }

  useEffect(() => {
    const active = mcpData.some((m) => m.status === 'active');
    window.dispatchEvent(new CustomEvent('grigo-chat-mcp-active', { detail: active }));
  }, [mcpData]);

  const ensureCompatibleModel = useCallback(
    (ctx: ChatFeatureContext, featureLabel: string) => {
      if (!hasActiveFeatureRequirements(ctx)) {
        return true;
      }
      let currentModal: { id?: number; name?: string } | undefined;
      try {
        const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
          provider_id: number;
          data?: { modal?: { id?: number; name?: string } };
        }>;
        currentModal = stored.find((entry) => entry.provider_id === activeProvider)?.data?.modal;
      } catch {
        currentModal = undefined;
      }
      const pick = pickCompatibleModel({
        models: llmPermissions,
        ctx,
        current: {
          id: currentModal?.id,
          name: currentModal?.name,
          providerId: activeProvider,
        },
      });
      if (!pick.ok) {
        MakeToast('error', `None of your assigned models support ${featureLabel}.`);
        return false;
      }
      if (pick.switched) {
        const applied = applyPickedChatModel({
          model: pick.model,
          currentProviderId: activeProvider,
          setChatModel,
          setActiveProvider,
        });
        const providerLabel = pick.model.provider_name ? ` (${pick.model.provider_name})` : '';
        MakeToast('success', `Switched to ${pick.model.name}${providerLabel}`);
        if (applied.sessionId && pick.model.name) {
          UpdateModelForProvider({
            sessionId: applied.sessionId,
            model: pick.model.name,
            type: 'Switch-Model',
          }).catch(() => {});
        }
      }
      return true;
    },
    [activeProvider, llmPermissions, setActiveProvider, setChatModel],
  );

  useEffect(() => {
    if (!llmPermissionsLoaded || !llmPermissions.length) {
      return;
    }
    const mcpActive = mcpData.some((m) => m.status === 'active');
    if (!mcpActive) {
      return;
    }
    ensureCompatibleModel(
      {
        thinkingEnabled: Boolean(state.thinkingEnabled),
        webSearchEnabled: Boolean(state.webSearchEnabled),
        codeInterpreterEnabled: Boolean(state.codeInterpreterEnabled),
        mcpActive: true,
      },
      'MCP',
    );
  }, [
    mcpData,
    llmPermissions,
    llmPermissionsLoaded,
    state.thinkingEnabled,
    state.webSearchEnabled,
    state.codeInterpreterEnabled,
    ensureCompatibleModel,
  ]);

  // Check if AI is currently responding (thinking or streaming)
  const isAiResponding = () => {
    if (state.newResponseLoader) return true;
    const lastMessage = state.chatHistory[state.chatHistory.length - 1];
    return (
      lastMessage?.role === 'AI' &&
      (lastMessage?.isThinking === true || lastMessage?.isStreaming === true)
    );
  };

  const aiIsResponding = isAiResponding();
  const isFileUploadInProgress = Boolean(state.isFileUploadPending);
  const isPiiConfirmationOpen = piiConfirmation.show;
  const isPendingFileNotReady = Boolean(state.pendingFile) && !pendingUploadedFileId;
  const isSendBlockedByFile =
    isFileUploadInProgress || isPiiConfirmationOpen || isPendingFileNotReady;
  const showPendingFileInQuery = Boolean(state.pendingFile);

  const setFileUploadPending = (pending: boolean) => {
    dispatch({ type: 'SET_FILE_UPLOAD_PENDING', payload: pending } as FileUploadPendingAction);
  };

  const clearPendingFileComposerState = () => {
    fileUploadRequestIdRef.current += 1;
    fileUploadAbortControllerRef.current?.abort();
    fileUploadAbortControllerRef.current = null;
    setFileUploadPending(false);
    setPendingUploadedFileId(null);
    pendingUploadSessionIdRef.current = null;
    dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const setGenerationUi = useCallback(
    (isGenerating: boolean) => {
      onGenerationUiChange?.({ isGenerating });
    },
    [onGenerationUiChange],
  );

  const isStreamSessionActive = useCallback((streamSessionId: number) => {
    return (
      !streamStoppedRef.current &&
      activeStreamFinalizeRef.current?.streamSessionId === streamSessionId
    );
  }, []);

  const beginStreamSession = useCallback(
    (chat: DataItem[], aiMessageIndex: number, persistChat: (updatedChat: DataItem[]) => void) => {
      const streamSessionId = streamSessionIdRef.current + 1;
      streamSessionIdRef.current = streamSessionId;
      streamStoppedRef.current = false;
      streamStopRequestedRef.current = false;
      if (stopFinalizeTimeoutRef.current != null) {
        window.clearTimeout(stopFinalizeTimeoutRef.current);
        stopFinalizeTimeoutRef.current = null;
      }
      streamAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      streamAbortControllerRef.current = abortController;
      activeStreamFinalizeRef.current = {
        chat,
        aiMessageIndex,
        persistChat,
        streamSessionId,
        tokenUsage: chat[aiMessageIndex]?.tokenUsage,
      };
      setGenerationUi(true);
      return { signal: abortController.signal, streamSessionId };
    },
    [setGenerationUi],
  );

  const trackStreamTokenUsage = useCallback((tokenUsage?: DataItem['tokenUsage']) => {
    if (tokenUsage && activeStreamFinalizeRef.current) {
      activeStreamFinalizeRef.current.tokenUsage = tokenUsage;
    }
  }, []);

  const endStreamSession = useCallback(
    (streamSessionId?: number) => {
      if (
        typeof streamSessionId === 'number' &&
        activeStreamFinalizeRef.current?.streamSessionId !== streamSessionId
      ) {
        return;
      }
      streamAbortControllerRef.current = null;
      activeStreamFinalizeRef.current = null;
      dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
      setGenerationUi(false);
    },
    [dispatch, setGenerationUi],
  );

  const persistActiveProviderChat = useCallback(
    (chat: DataItem[], sessionId?: string) => {
      try {
        const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
          provider_id: number;
          session_id?: string;
          data: Record<string, unknown> & { chatData?: DataItem[] };
        }>;
        const persisted = stored.map((entry) =>
          entry.provider_id === activeProvider
            ? {
                ...entry,
                session_id: sessionId ?? entry.session_id,
                data: {
                  ...entry.data,
                  chatData: chat,
                },
              }
            : entry,
        );
        localStorage.setItem('chat', JSON.stringify(persisted));
      } catch {
        MakeToast('error', 'Failed to save chat history');
      }
    },
    [activeProvider],
  );

  // Unlock composer if stream flags / loader were left stuck after a finished response
  useEffect(() => {
    if (isGenerationActive || activeStreamFinalizeRef.current) return undefined;

    const lastMessage = state.chatHistory[state.chatHistory.length - 1];
    const hasStuckStreamFlags =
      lastMessage?.role === 'AI' &&
      (lastMessage.isThinking === true || lastMessage.isStreaming === true);

    if (!hasStuckStreamFlags && !state.newResponseLoader) return undefined;

    const timeoutId = window.setTimeout(() => {
      if (isGenerationActive || activeStreamFinalizeRef.current) return;

      if (state.newResponseLoader) {
        dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
      }

      if (hasStuckStreamFlags) {
        const sanitized = sanitizeChatHistoryForHydration(state.chatHistory);
        dispatch({ type: 'SET_CHAT_HISTORY', payload: sanitized });
        persistActiveProviderChat(sanitized);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    state.chatHistory,
    state.newResponseLoader,
    isGenerationActive,
    dispatch,
    persistActiveProviderChat,
  ]);

  const chatHistoryRef = useRef(state.chatHistory);
  chatHistoryRef.current = state.chatHistory;

  const trimLastAiFromChat = useCallback((chat: DataItem[]) => {
    if (chat.length > 0 && chat[chat.length - 1]?.role === 'AI') {
      return chat.slice(0, -1);
    }
    return chat;
  }, []);

  const createStreamAiPlaceholder = useCallback(
    (): DataItem => ({
      role: 'AI',
      message: '',
      time: new Date(),
      type: 'Query',
      sources: [],
      webSearchUsed: false,
      webSearchQuery: undefined,
      generatedFiles: undefined,
      toolCalls: undefined,
      mcpWarnings: undefined,
      isThinking: true,
      isStreaming: true,
      thinkingText: '',
      tokenUsage: undefined,
      generationStopped: false,
      stream_end: false,
    }),
    [],
  );

  const findLastAiMessageIndex = useCallback((chat: DataItem[]) => {
    for (let i = chat.length - 1; i >= 0; i -= 1) {
      if (chat[i]?.role === 'AI') return i;
    }
    return -1;
  }, []);

  const prepareChatForRetry = useCallback(
    (sessionId: string, queryText: string) => {
      const currentChat = chatHistoryRef.current.length ? [...chatHistoryRef.current] : [];
      const aiIndex = findLastAiMessageIndex(currentChat);

      if (aiIndex >= 0) {
        const chatForRetry = [...currentChat];

        if (queryText) {
          const humanIndex = aiIndex - 1;
          if (humanIndex >= 0 && chatForRetry[humanIndex]?.role === 'Human') {
            chatForRetry[humanIndex] = {
              ...chatForRetry[humanIndex],
              message: queryText,
            };
          }
        }

        chatForRetry[aiIndex] = createStreamAiPlaceholder();
        retryAiMessageIndexRef.current = aiIndex;
        skipAppendAiOnRetryRef.current = true;
        skipAddHumanOnNextSendRef.current = true;
        retryBaseChatRef.current = null;
        retryPreparedChatRef.current = chatForRetry;

        dispatch({ type: 'SET_CHAT_HISTORY', payload: chatForRetry });
        persistActiveProviderChat(chatForRetry, sessionId);
        return chatForRetry;
      }

      let chatWithoutAi = trimLastAiFromChat(currentChat);
      const lastMessage = chatWithoutAi[chatWithoutAi.length - 1];
      if (queryText) {
        if (lastMessage?.role === 'Human' && !hasMessageText(lastMessage.message)) {
          chatWithoutAi = [...chatWithoutAi.slice(0, -1), { ...lastMessage, message: queryText }];
        } else if (lastMessage?.role !== 'Human') {
          chatWithoutAi = [
            ...chatWithoutAi,
            {
              role: 'Human',
              message: queryText,
              time: new Date(),
              type: 'Query',
            },
          ];
        }
      }

      retryBaseChatRef.current = chatWithoutAi;
      retryAiMessageIndexRef.current = null;
      skipAppendAiOnRetryRef.current = false;
      skipAddHumanOnNextSendRef.current = true;
      dispatch({ type: 'SET_CHAT_HISTORY', payload: chatWithoutAi });
      persistActiveProviderChat(chatWithoutAi, sessionId);
      return chatWithoutAi;
    },
    [
      createStreamAiPlaceholder,
      dispatch,
      findLastAiMessageIndex,
      persistActiveProviderChat,
      trimLastAiFromChat,
    ],
  );

  const getActiveSessionId = useCallback((): string | null => {
    if (retryContextRef.current?.sessionId) {
      return retryContextRef.current.sessionId;
    }
    try {
      const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
        provider_id: number;
        session_id?: string;
      }>;
      return stored.find((entry) => entry.provider_id === activeProvider)?.session_id ?? null;
    } catch {
      return null;
    }
  }, [activeProvider]);

  const shouldTerminateStreamOnServer = useCallback(() => {
    const providerName = llmPermissions
      .find((permission) => permission.proverID === activeProvider)
      ?.provider_name?.toLowerCase();
    return providerName === 'openai' || providerName === 'google';
  }, [activeProvider, llmPermissions]);

  const finalizeStoppedStream = useCallback(() => {
    const session = activeStreamFinalizeRef.current;
    let sourceChat: DataItem[] = [];

    if (session?.chat?.length) {
      sourceChat = [...session.chat];
    } else if (chatHistoryRef.current.length) {
      sourceChat = [...chatHistoryRef.current];
    }
    if (!sourceChat.length) return;

    let aiMessageIndex = session?.aiMessageIndex ?? -1;
    if (aiMessageIndex < 0) {
      for (let i = sourceChat.length - 1; i >= 0; i -= 1) {
        if (sourceChat[i]?.role === 'AI') {
          aiMessageIndex = i;
          break;
        }
      }
    }
    if (aiMessageIndex < 0) return;

    const currentMsg = sourceChat[aiMessageIndex];
    const hasAiResponse = hasMessageText(currentMsg?.message);
    const finalChat = [...sourceChat];
    const resolvedTokenUsage =
      currentMsg?.tokenUsage ??
      session?.tokenUsage ??
      chatHistoryRef.current[aiMessageIndex]?.tokenUsage;

    if (!hasAiResponse) {
      // User stopped before receiving any AI response; remove placeholder entirely.
      finalChat.splice(aiMessageIndex, 1);
    } else {
      finalChat[aiMessageIndex] = {
        ...currentMsg,
        isThinking: false,
        isStreaming: false,
        generationStopped: true,
        stream_end: true,
        message: currentMsg?.message ?? '',
        thinkingText: currentMsg?.thinkingText ?? '',
        ...(resolvedTokenUsage ? { tokenUsage: resolvedTokenUsage } : {}),
      };
    }

    dispatch({ type: 'SET_CHAT_HISTORY', payload: finalChat });
    if (session?.persistChat) {
      session.persistChat(finalChat);
    } else {
      persistActiveProviderChat(finalChat);
    }
    setIsContentAvailable(true);
    dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
    endStreamSession(session?.streamSessionId);
    if (resolvedTokenUsage) {
      window.dispatchEvent(new CustomEvent('chatResponseComplete'));
    }
  }, [dispatch, endStreamSession, persistActiveProviderChat, setIsContentAvailable]);

  const completeStopAfterTokens = useCallback(() => {
    if (streamStoppedRef.current || !streamStopRequestedRef.current) return;
    if (stopFinalizeTimeoutRef.current != null) {
      window.clearTimeout(stopFinalizeTimeoutRef.current);
      stopFinalizeTimeoutRef.current = null;
    }
    streamStoppedRef.current = true;
    streamStopRequestedRef.current = false;
    finalizeStoppedStream();
  }, [finalizeStoppedStream]);

  const stopGeneration = useCallback(() => {
    if (!streamAbortControllerRef.current || streamStoppedRef.current) return;

    if (shouldTerminateStreamOnServer()) {
      const sessionId = getActiveSessionId();
      if (sessionId) {
        terminateChatStream(sessionId).catch(() => {
          MakeToast('error', 'Failed to stop generation on server');
        });
      }

      const session = activeStreamFinalizeRef.current;
      const aiMessageIndex = session?.aiMessageIndex ?? -1;
      const currentMsg = aiMessageIndex >= 0 ? session?.chat?.[aiMessageIndex] : undefined;
      const hasAiResponse = hasMessageText(currentMsg?.message);

      if (!hasAiResponse) {
        streamStoppedRef.current = true;
        finalizeStoppedStream();
      } else {
        streamStopRequestedRef.current = true;
        stopFinalizeTimeoutRef.current = window.setTimeout(() => {
          completeStopAfterTokens();
        }, 750);
      }

      return;
    }

    streamStoppedRef.current = true;
    finalizeStoppedStream();
    streamAbortControllerRef?.current?.abort();
  }, [
    completeStopAfterTokens,
    finalizeStoppedStream,
    getActiveSessionId,
    shouldTerminateStreamOnServer,
  ]);

  const retryGeneration = useCallback(() => {
    if (retryRunnerRef.current) {
      retryRunnerRef.current();
      return;
    }

    const history = chatHistoryRef.current || [];
    let lastHuman: DataItem | undefined;
    for (let i = history.length - 1; i >= 0; i -= 1) {
      if (history[i]?.role === 'Human') {
        lastHuman = history[i];
        break;
      }
    }

    const rawMessage = lastHuman?.message;
    let queryText = '';
    if (typeof rawMessage === 'string') {
      queryText = rawMessage;
    } else if (Array.isArray(rawMessage)) {
      queryText = rawMessage.filter((part): part is string => typeof part === 'string').join('\n');
    }

    if (!lastHuman || !queryText.trim()) {
      MakeToast('error', 'Nothing to retry');
      return;
    }

    if (isFileUploadHumanMessage(lastHuman)) {
      MakeToast('error', 'Resend the file to retry');
      return;
    }

    const sessionId = getActiveSessionId();
    if (!sessionId) {
      MakeToast('error', 'Session not found. Please send a new message.');
      return;
    }

    retryContextRef.current = { sessionId, queryText };
    pendingRetryQueryRef.current = queryText;
    prepareChatForRetry(sessionId, queryText);
    handleSendQueryRef.current?.();
  }, [getActiveSessionId, prepareChatForRetry]);

  useEffect(() => {
    const streamRef = streamControlRef;

    if (!streamRef) return undefined;

    streamRef.current = {
      stop: stopGeneration,
      retry: retryGeneration,
    };

    return () => {
      streamRef.current = null;
    };
  }, [streamControlRef, stopGeneration, retryGeneration]);
  // Extract plain text from contentEditable
  const getPlainText = (element: HTMLElement | null): string => {
    if (!element) return '';
    return element.innerText || element.textContent || '';
  };

  // Empty for placeholder: only truly empty / lone <br> newline — not whitespace-only typed content
  const isContentEditableEmpty = (text: string): boolean => {
    return text === '' || text === '\n';
  };

  const normalizeEmptyContentEditable = (element: HTMLElement) => {
    const editable = element;
    if (editable.innerHTML !== '<br>') {
      editable.innerHTML = '<br>';
    }
    editable.classList.add('empty-content');
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(editable, 0);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  // Format query with styled mentions
  const formatQueryWithMentions = (text: string): string => {
    if (!text) return '';

    // Find all @agent mentions - match @ followed by agent name (word characters, spaces, hyphens, underscores)
    // Stop at space, newline, or end of string
    const mentionRegex = /@([^\s\n]+)/g;
    let lastIndex = 0;
    const parts: Array<{ text: string; isMention: boolean }> = [];

    let match = mentionRegex.exec(text);
    while (match !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          text: text.substring(lastIndex, match.index),
          isMention: false,
        });
      }

      // Check if this is a valid agent mention
      const agentName = match[1];
      const isAgent = agents.some((agent) => agent.agent_name === agentName);

      if (isAgent) {
        parts.push({
          text: match[0],
          isMention: true,
        });
      } else {
        parts.push({
          text: match[0],
          isMention: false,
        });
      }

      lastIndex = match.index + match[0].length;
      match = mentionRegex.exec(text);
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        text: text.substring(lastIndex),
        isMention: false,
      });
    }

    // Build HTML - escape HTML in non-mention text
    const escapeHtml = (str: string) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    return parts
      .map((part) => {
        if (part.isMention) {
          return `<span class="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-medium inline-block">${escapeHtml(part.text)}</span>`;
        }
        return escapeHtml(part.text).replace(/\n/g, '<br>');
      })
      .join('');
  };

  // Get cursor position in plain text from contentEditable
  const getCursorPosition = (element: HTMLElement): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  };

  // Handle contentEditable input
  const handleContentEditableInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (showLlmSetupBanner) return;
    const element = e.currentTarget;
    let plainText = getPlainText(element);
    let cursorPosition = getCursorPosition(element);

    // Update empty-content class for placeholder visibility.
    // Do not use trim() — a typed space must hide the placeholder or the caret
    // lands at the end of the ::before placeholder text.
    if (isContentEditableEmpty(plainText)) {
      normalizeEmptyContentEditable(element);
      plainText = '';
      cursorPosition = 0;
      skipContentEditableSyncRef.current = true;
      dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
      setShowAgentMention(false);
      setMentionQuery('');
      setMentionStartIndex(-1);
      return;
    }

    element.classList.remove('empty-content');
    skipContentEditableSyncRef.current = true;
    dispatch({ type: 'SET_QUERY_VALUE', payload: plainText });

    // Check for @ mention
    const textBeforeCursor = plainText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if @ is part of a mention (not part of email or other text)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpaceAfterAt = textAfterAt.includes(' ');
      const hasNewlineAfterAt = textAfterAt.includes('\n');

      if (!hasSpaceAfterAt && !hasNewlineAfterAt) {
        // Text after @ (preserve case for correct replace length; filter compares case-insensitive)
        setMentionQuery(textAfterAt);
        setMentionStartIndex(lastAtIndex);
        setShowAgentMention(true);
        setSelectedMentionIndex(0);
        return;
      }
    }

    // Hide mention dropdown if @ is not found or is part of other text
    setShowAgentMention(false);
    setMentionQuery('');
    setMentionStartIndex(-1);
  };

  // Update contentEditable when query value changes externally
  useEffect(() => {
    if (!contentEditableRef.current) return;

    // Typing already updated the DOM — rewriting here drops the caret to the start
    // (e.g. "t" in state vs "t\\n" from a leftover <br> after clearing).
    if (skipContentEditableSyncRef.current) {
      skipContentEditableSyncRef.current = false;
      return;
    }

    const currentText = getPlainText(contentEditableRef.current);
    const queryValue = state.queryValue || '';
    // Only update if the plain text differs (avoid infinite loops)
    const normalizedCurrent = isContentEditableEmpty(currentText) ? '' : currentText;
    const normalizedQuery = isContentEditableEmpty(queryValue) ? '' : queryValue;
    if (normalizedCurrent !== normalizedQuery) {
      const formatted = formatQueryWithMentions(queryValue);
      const selection = window.getSelection();
      const range =
        selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

      contentEditableRef.current.innerHTML = formatted || '<br>';

      // Update empty-content class for placeholder visibility
      if (isContentEditableEmpty(queryValue)) {
        contentEditableRef.current.classList.add('empty-content');
      } else {
        contentEditableRef.current.classList.remove('empty-content');
      }

      // Restore cursor position if possible
      if (range && contentEditableRef.current.contains(range.startContainer)) {
        try {
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (e) {
          // If range is invalid, just focus the element
          contentEditableRef.current.focus();
        }
      }
    }
  }, [state.queryValue, agents]);

  const dataArray = localStorage.getItem(`chat`);
  let parsedData:
    | [
        {
          provider_id: number;
          data: {
            use_document: boolean | string;
            has_document: boolean;
            chatData: Array<object>;
            modal?: { name: string; id: number };
          };
        },
      ]
    | null = null;
  if (dataArray !== null) {
    try {
      parsedData = JSON.parse(dataArray);
    } catch {
      MakeToast('error', 'Error parsing chat data!');
    }
  }

  const history = localStorage.getItem('chat');
  let parsedHistory: {
    data: {
      chatData: [];
      use_document: boolean;
      modal: { name: string; id: number };
      file_id: string;
      file_type?: string;
      webSearchEnabled?: boolean;
      webSearchMode?: WebSearchMode;
      codeInterpreterEnabled?: boolean;
      has_document?: boolean;
    };
    provider_id: number;
    session_id: string;
  }[] = history ? JSON.parse(history) : [];

  let currentProviderData = parsedHistory.find(
    (entry: {
      data: {
        chatData: [];
        use_document: boolean;
        modal: { name: string };
        file_id: string;
        file_type?: string;
        webSearchEnabled?: boolean;
        webSearchMode?: WebSearchMode;
        codeInterpreterEnabled?: boolean;
        has_document?: boolean;
      };
      provider_id: number;
      session_id: string;
    }) => entry.provider_id === activeProvider,
  );
  const webSearchMode = resolveWebSearchMode(currentProviderData?.data);
  const webSearchEnabled = webSearchMode === 'on';
  const codeInterpreterEnabled = currentProviderData?.data?.codeInterpreterEnabled || false;
  const selectedChatModelName =
    typeof currentProviderData?.data?.modal === 'object'
      ? currentProviderData?.data?.modal?.name
      : currentProviderData?.data?.modal;
  const selectedPermissionModel = llmPermissions.find((item) => {
    if (!selectedChatModelName || item.name !== selectedChatModelName) {
      return false;
    }
    if (activeProvider == null) {
      return true;
    }
    return Number(item.proverID ?? item.provider_id) === Number(activeProvider);
  });
  const isSelectedModelAdminBlocked = isAdminBlockedModelStatus(
    selectedPermissionModel?.model_status,
  );

  const isExcelFile = (fileType?: string, fileName?: string) => {
    const normalizedType = (fileType || '').toLowerCase();
    const normalizedName = (fileName || '').toLowerCase();
    return (
      normalizedType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      normalizedType === 'application/vnd.ms-excel' ||
      normalizedName.endsWith('.xlsx') ||
      normalizedName.endsWith('.xls')
    );
  };

  const needsCodeInterpreterFile = (fileType?: string, fileName?: string) => {
    const normalizedType = (fileType || '').toLowerCase();
    const normalizedName = (fileName || '').toLowerCase();
    return (
      isExcelFile(fileType, fileName) ||
      normalizedType === 'text/csv' ||
      normalizedName.endsWith('.csv')
    );
  };

  const totals = state.chatHistory.reduce(
    (acc, msg) => {
      if (msg.role === 'AI' && msg.tokenUsage) {
        acc.input += msg.tokenUsage.input_tokens ?? 0;
        acc.output += msg.tokenUsage.output_tokens ?? 0;
        acc.reasoning += msg.tokenUsage.reasoning_tokens ?? 0;
      }
      return acc;
    },
    {
      input: 0,
      output: 0,
      reasoning: 0,
    },
  );

  const grandTotalTokens = totals.input + totals.output + totals.reasoning;

  const showDeleteMcpConfirmation = (mcpId: number, mcpName: string) => {
    setDeleteMcpConfirmation({ show: true, mcpId, mcpName });
  };

  const handleDeleteMcp = async () => {
    if (!deleteMcpConfirmation.mcpId) return;
    const { mcpId } = deleteMcpConfirmation;
    DeleteMcpChat(mcpId)
      .then((res) => {
        if (res.status === 200) {
          makeToast('success', 'MCP configuration deleted successfully');
          allMcpApi();
          setDeleteMcpConfirmation({ show: false, mcpId: null, mcpName: '' });
        }
      })
      .catch((error: ApiError) => {
        if (error?.response?.data?.status === 401) {
          setLogout(true);
        } else {
          makeToast('error', 'Failed to delete MCP configuration');
        }
        setDeleteMcpConfirmation({ show: false, mcpId: null, mcpName: '' });
      });
  };

  const handleUpdateMcpStatus = async (mcpId: number, currentStatus: string) => {
    const mcp = mcpData.find((m) => m.id === mcpId);
    if (!mcp || !mcpCanUserToggle(currentStatus)) {
      return;
    }

    const turningOff = currentStatus === 'active';
    if (!turningOff) {
      const canEnable = ensureCompatibleModel(
        {
          thinkingEnabled: Boolean(state.thinkingEnabled),
          webSearchEnabled: Boolean(state.webSearchEnabled),
          codeInterpreterEnabled: Boolean(state.codeInterpreterEnabled),
          mcpActive: true,
        },
        'MCP',
      );
      if (!canEnable) {
        return;
      }
    }
    const payload = {
      status: turningOff ? 'inactive' : 'active',
      disabled_tools: mcp.disabled_tools || [],
    };
    EditMcpChatServce(payload, mcpId)
      .then((res) => {
        if (res.status === 200) {
          if (!turningOff) {
            invalidateMcpWidgetHtmlCache(mcpId);
          }
          clearMcpPanelMessage(mcpId);
          makeToast('success', turningOff ? 'MCP turned off' : 'MCP turned on');
          allMcpApi();
        }
      })
      .catch((error: ApiError) => {
        if (error?.response?.data?.status === 401) {
          setLogout(true);
        }
      });
  };

  const handleRefreshMcpTools = async (mcpId: number) => {
    setMcpRefreshingId(mcpId);
    try {
      const res = await RefreshMcpToolsService(mcpId);
      if (res.status === 200) {
        makeToast('success', 'MCP tools refreshed');
        invalidateMcpWidgetHtmlCache(mcpId);
        clearMcpPanelMessage(mcpId);
        await allMcpApi();
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError?.response?.data?.status === 401) {
        setLogout(true);
      } else {
        const message = apiError?.response?.data?.message;
        const mcp = mcpData.find((entry) => entry.id === mcpId);
        const failureText =
          typeof message === 'string'
            ? formatMcpSidePanelFailure(message)
            : `${mcp?.name ?? 'MCP'} could not refresh tools. Use Reconnect to open MCP settings.`;
        setMcpPanelMessages((prev) => ({ ...prev, [mcpId]: failureText }));
        setExpandedMcp(mcpId);
        makeToast(
          'error',
          typeof message === 'string'
            ? formatMcpUserNotice(message)
            : 'Failed to refresh MCP tools',
        );
        await allMcpApi();
      }
    } finally {
      setMcpRefreshingId(null);
    }
  };

  const handleUpdateMcpTool = async (
    mcpId: number,
    tool: string,
    currentDisabledTools: string[],
    currentStatus: string,
  ) => {
    const isCurrentlyDisabled = currentDisabledTools.includes(tool);
    const updatedDisabledTools = isCurrentlyDisabled
      ? currentDisabledTools.filter((t) => t !== tool)
      : [...currentDisabledTools, tool];

    const payload = {
      status: currentStatus,
      disabled_tools: updatedDisabledTools,
    };

    EditMcpChatServce(payload, mcpId)
      .then((res) => {
        if (res.status === 200) {
          makeToast('success', `Tool ${isCurrentlyDisabled ? 'enabled' : 'disabled'} successfully`);
          allMcpApi();
        }
      })
      .catch((error: ApiError) => {
        makeToast('error', 'Failed to update tool status');
        if (error?.response?.data?.status === 401) {
          setLogout(true);
        }
      });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Common stream event handler factory
  type StreamEventHandlerParams = {
    accumulatedText: { current: string };
    accumulatedThinking: { current: string };
    sources: { current: DataItem['sources'] };
    webSearchUsed: { current: boolean };
    webSearchQuery: { current: string };
    generatedFiles: { current: DataItem['generatedFiles'] };
    tokenUsage: {
      current: {
        input_tokens?: number;
        reasoning_tokens?: number;
        output_tokens?: number;
      };
    };
    toolCalls: { current: ToolCall[] };
    mcpWarnings: { current: string[] };
    updateAiMessage: (partial: Partial<DataItem>) => void;
    onDone?: () => void;
    onError?: (error: string) => void;
    aiPlaceholder: DataItem;
    isStreamStopped?: () => boolean;
  };

  const createStreamEventHandler = (params: StreamEventHandlerParams) => {
    const thinkingStartedAt = { current: 0 };
    const thinkingSeconds = () => {
      if (!thinkingStartedAt.current) return undefined;
      return Math.max(1, Math.round((Date.now() - thinkingStartedAt.current) / 1000));
    };
    const {
      accumulatedText,
      accumulatedThinking,
      sources,
      webSearchUsed,
      webSearchQuery,
      generatedFiles,
      tokenUsage,
      toolCalls,
      mcpWarnings,
      updateAiMessage,
      onDone,
      onError,
      isStreamStopped: checkStreamStopped,
    } = params;

    return (event: StreamEvent) => {
      if (event?.type === 'token_usage') {
        tokenUsage.current = {
          input_tokens: event.input_tokens as number,
          reasoning_tokens: event.reasoning_tokens as number,
          output_tokens: event.output_tokens as number,
        };
        updateAiMessage({ tokenUsage: tokenUsage.current });
        if (streamStopRequestedRef.current) {
          completeStopAfterTokens();
        }
        return;
      }

      if (checkStreamStopped?.()) return;

      try {
        switch (event?.type) {
          case 'thinking':
            if (event.type === 'thinking') {
              if (!thinkingStartedAt.current) thinkingStartedAt.current = Date.now();
              accumulatedThinking.current += event.delta;
              updateAiMessage({
                isThinking: true,
                isStreaming: false,
                thinkingText: accumulatedThinking.current,
                thinkingDurationSec: thinkingSeconds(),
                message: '',
              });
            }
            break;

          case 'output':
            if (event.type === 'output') {
              accumulatedText.current += event.delta;
              updateAiMessage({
                isThinking: false,
                isStreaming: true,
                message: accumulatedText.current,
                thinkingText: accumulatedThinking.current || '',
                thinkingDurationSec: thinkingSeconds(),
              });
            }
            break;

          case 'sources':
            if (event.type === 'sources') {
              sources.current = mergeWebSearchSources(
                sources.current,
                event.sources as DataItem['sources'],
              );
              updateAiMessage({ sources: sources.current });
            }
            break;

          case 'web_search_started':
            if (event.type === 'web_search_started') {
              const query = typeof event.query === 'string' ? event.query : '';
              webSearchQuery.current = query;
              updateAiMessage({ webSearchQuery: query });
            }
            break;

          case 'web_search_used':
            if (event.type === 'web_search_used') {
              webSearchUsed.current = true;
              updateAiMessage({ webSearchUsed: true });
            }
            break;

          case 'routing_decision':
            if (event.type === 'routing_decision' && event.model_name) {
              updateAiMessage({ routedModelName: String(event.model_name) });
              window.dispatchEvent(
                new CustomEvent('grigo-routing-model', { detail: event.model_name }),
              );
              window.dispatchEvent(
                new CustomEvent('grigo-routing-detail', {
                  detail: {
                    model_name: event.model_name,
                    routing_band: event.resolved_routing_band ?? event.routing_band,
                    target_routing_band: event.target_routing_band,
                    resolved_routing_band: event.resolved_routing_band,
                    routing_score: event.routing_score,
                    logical_model: event.logical_model,
                    selection_reason: event.selection_reason,
                    pick_tier: event.pick_tier,
                  },
                }),
              );
            }
            break;

          case 'generated_files':
            if (event.type === 'generated_files') {
              generatedFiles.current = event.generated_files as DataItem['generatedFiles'];
              updateAiMessage({ generatedFiles: generatedFiles.current });
            }
            break;

          case 'mcp_call':
          case 'tool_call_started':
          case 'function_call_started':
          case 'tool_call':
          case 'function_call': {
            const toolEvent = event as {
              call_id?: string;
              tool_name?: string;
              delta?: string;
              id?: string;
              mcp_id?: number;
              has_widget?: boolean;
              ui_resource_uri?: string;
            };
            const toolName = toolEvent.tool_name || toolEvent.call_id;
            const toolId = toolEvent.id || toolEvent.call_id;
            if (toolName) {
              const existingIndex = toolCalls.current.findIndex(
                (tc) => tc.id === toolId && tc.tool_name === toolName,
              );
              const existing = existingIndex >= 0 ? toolCalls.current[existingIndex] : undefined;
              const isMcpStart =
                event.type === 'mcp_call' ||
                event.type === 'tool_call_started' ||
                event.type === 'function_call_started';
              const nextToolCall: ToolCall = {
                tool_name: toolName,
                message: (existing?.message || '') + (toolEvent.delta || ''),
                id: toolId ?? existing?.id,
                mcp_id: toolEvent.mcp_id ?? existing?.mcp_id,
                has_widget: toolEvent.has_widget ?? existing?.has_widget,
                ui_resource_uri: toolEvent.ui_resource_uri ?? existing?.ui_resource_uri,
                widget_arguments: existing?.widget_arguments,
                widget_result: existing?.widget_result,
                is_loading: isMcpStart ? true : existing?.is_loading,
              };
              if (existingIndex >= 0) {
                toolCalls.current[existingIndex] = nextToolCall;
              } else {
                toolCalls.current.push(nextToolCall);
              }
              updateAiMessage({ toolCalls: [...toolCalls.current] });
            }
            break;
          }

          case 'function_call_code':
          case 'tool_code':
          case 'tool_call_code': {
            const toolEvent = event as {
              call_id?: string;
              tool_name?: string;
              delta?: string | Record<string, unknown>;
              id?: string;
            };
            const toolName = toolEvent.tool_name || toolEvent.call_id;
            const toolId = toolEvent.id || toolEvent.call_id;
            if (toolName && toolEvent.delta) {
              let deltaContent = '';
              if (typeof toolEvent.delta === 'string') {
                deltaContent = toolEvent.delta;
              } else if (typeof toolEvent.delta === 'object') {
                deltaContent = JSON.stringify(toolEvent.delta, null, 2);
              }
              const existingIndex = toolCalls.current.findIndex(
                (tc) => tc.id === toolId && tc.tool_name === toolName,
              );
              if (existingIndex >= 0) {
                toolCalls.current[existingIndex] = {
                  ...toolCalls.current[existingIndex],
                  message: toolCalls.current[existingIndex].message + deltaContent,
                };
              } else {
                toolCalls.current.push({
                  tool_name: toolName,
                  message: deltaContent,
                  id: toolId,
                });
              }
              updateAiMessage({ toolCalls: [...toolCalls.current] });
            }
            break;
          }

          case 'mcp_tool_result': {
            const resultEvent = event as {
              tool_name?: string;
              tool_id?: string;
              mcp_id?: number;
              result?: unknown;
              error?: unknown;
              arguments?: unknown;
            };
            if (resultEvent.tool_name) {
              toolCalls.current = patchToolCallResult(toolCalls.current, {
                tool_name: resultEvent.tool_name,
                tool_id: resultEvent.tool_id,
                mcp_id: resultEvent.mcp_id,
                result: resultEvent.result,
                error: resultEvent.error,
                arguments: resultEvent.arguments,
              });
              updateAiMessage({ toolCalls: [...toolCalls.current] });
              if (resultEvent.error) {
                handleMcpToolResultFailure(resultEvent).catch(() => undefined);
              }
            }
            break;
          }

          case 'mcp_connection_failed': {
            const failureEvent = event as {
              message?: string;
              mcp_id?: number;
            };
            handleMcpConnectionFailed(failureEvent).catch(() => undefined);
            break;
          }

          case 'mcp_widget_result': {
            const widgetEvent = event as {
              tool_name?: string;
              id?: string;
              mcp_id?: number;
              resource_uri?: string;
              result?: unknown;
              arguments?: unknown;
            };
            if (widgetEvent.tool_name) {
              toolCalls.current = patchToolCallResult(toolCalls.current, {
                tool_name: widgetEvent.tool_name,
                tool_id: widgetEvent.id,
                mcp_id: widgetEvent.mcp_id,
                result: widgetEvent.result,
                arguments: widgetEvent.arguments,
                mergeResult: true,
                has_widget: true,
                ui_resource_uri: widgetEvent.resource_uri,
              });
              updateAiMessage({ toolCalls: [...toolCalls.current] });
            }
            break;
          }

          case 'tool_code_done':
          case 'tool_completed':
            if (toolCalls.current.length > 0) {
              toolCalls.current = finalizeToolCalls(toolCalls.current);
              updateAiMessage({ toolCalls: [...toolCalls.current] });
            }
            break;

          case 'done':
            if (toolCalls.current.length > 0) {
              toolCalls.current = finalizeToolCalls(toolCalls.current);
            }
            updateAiMessage({
              isThinking: false,
              isStreaming: false,
              generationStopped: false,
              stream_end: true,
              message: accumulatedText.current,
              thinkingText: accumulatedThinking.current || '',
              thinkingDurationSec: thinkingSeconds(),
              sources: sources.current,
              webSearchUsed: webSearchUsed.current,
              webSearchQuery: webSearchQuery.current || undefined,
              generatedFiles: generatedFiles.current,
              toolCalls: toolCalls.current.length > 0 ? toolCalls.current : undefined,
              mcpWarnings: mcpWarnings.current.length > 0 ? mcpWarnings.current : undefined,
              tokenUsage: tokenUsage.current,
            });
            onDone?.();
            break;

          case 'mcp_warning': {
            const warning = (event as { message?: string }).message;
            if (warning) {
              if (isMcpConnectivityMessage(warning)) {
                showMcpFailureInSidePanel({ message: warning }).catch(() => undefined);
              } else {
                handleMcpStreamNotice(warning);
                mcpWarnings.current = appendMcpWarning(mcpWarnings.current, warning);
                updateAiMessage({
                  mcpWarnings: mcpWarnings.current.length > 0 ? mcpWarnings.current : undefined,
                });
              }
            }
            break;
          }

          case 'error':
            onError?.((event as { error?: string }).error || 'An error occurred');
            break;

          default:
            // Handle unknown event types
            break;
        }
      } catch (error) {
        onError?.('Error processing stream event');
      }
    };
  };

  // Clear pending file composer state after file is committed to chat (or discarded)
  const clearFileUploadUiState = () => {
    setFileUploadPending(false);
    setPendingUploadedFileId(null);
    pendingUploadSessionIdRef.current = null;
    dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Commit an already-uploaded file into chat history and optionally send the query
  const commitUploadedFileToChat = (
    sessionIdToUse: string,
    file: File,
    fileId: string,
    queryText?: string,
  ) => {
    setIsContentAvailable(true);
    localStorage.setItem('knowledge_base', JSON.stringify(false));
    dispatch({ type: 'SET_RAG', payload: false });

    const fileMetaData = {
      name: file.name,
      size: file.size,
      type: file.type,
    };

    const size = formatFileSize(file.size);
    const messageText = buildFileUploadMessageText(fileMetaData.name, size, queryText);

    const newChat: ChatDataItem[] = [
      ...(currentProviderData?.data?.chatData || []),
      {
        role: 'Human',
        message: messageText,
        time: new Date().toUTCString(),
        type: fileMetaData.type,
      },
    ];

    const updatedChat = parsedHistory.map((entry) =>
      entry.provider_id === activeProvider
        ? {
            ...entry,
            session_id: sessionIdToUse,
            data: {
              ...entry.data,
              chatData: newChat,
              use_document: true,
              has_document: true,
            },
          }
        : entry,
    );
    localStorage.setItem('chat', JSON.stringify(updatedChat));
    dispatch({ type: 'SET_CHAT_HISTORY', payload: newChat });
    dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
    dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: true });

    dispatch({ type: 'SET_CHAT_HAS_DOCUMENT', payload: true });
    dispatch({ type: 'SET_USE_DOCUMENT_ONLY', payload: true });

    // If queryText is provided, call chat API with file_id
    if (queryText && queryText.trim() !== '') {
      // Update chat data with file_id
      const updatedChatsWithFileId = parsedHistory.map((entry) =>
        entry.provider_id === activeProvider
          ? {
              ...entry,
              session_id: sessionIdToUse,
              data: {
                ...entry.data,
                chatData: newChat,
                use_document: true,
                has_document: true,
                file_id: fileId,
                file_type: fileMetaData.type,
              },
            }
          : entry,
      );
      localStorage.setItem('chat', JSON.stringify(updatedChatsWithFileId));

      // Get necessary variables for chat API call
      const useDocument = true; // File is uploaded, so use document
      const model =
        (typeof currentProviderData?.data?.modal !== 'object'
          ? currentProviderData?.data?.modal
          : currentProviderData?.data?.modal?.name) || '';

      const aiPlaceholder: DataItem = {
        ...createStreamAiPlaceholder(),
        type: fileMetaData.type,
      };

      const aiMessageIndex = newChat.length;
      let chatWithPlaceholder: DataItem[] = [...(newChat as DataItem[]), aiPlaceholder];

      dispatch({ type: 'SET_CHAT_HISTORY', payload: chatWithPlaceholder });

      const updatedDataWithPlaceholder = parsedHistory.map((entry) =>
        entry.provider_id === activeProvider
          ? {
              ...entry,
              session_id: sessionIdToUse,
              data: {
                ...entry.data,
                chatData: chatWithPlaceholder,
                use_document: true,
                has_document: true,
                file_id: fileId,
                file_type: fileMetaData.type,
              },
            }
          : entry,
      );
      localStorage.setItem('chat', JSON.stringify(updatedDataWithPlaceholder));
      // Variables for streaming
      const accumulatedText = { current: '' };
      const accumulatedThinking = { current: '' };
      const sources = { current: [] as DataItem['sources'] };
      const generatedFiles = { current: undefined as DataItem['generatedFiles'] };
      const webSearchUsed = { current: false };
      const webSearchQuery = { current: '' };
      const tokenUsage = {
        current: {} as {
          input_tokens?: number;
          reasoning_tokens?: number;
          output_tokens?: number;
        },
      };
      const toolCalls = {
        current: [] as Array<{ tool_name: string; message: string; id?: string }>,
      };
      const mcpWarnings = {
        current: [] as string[],
      };

      const persistFileChatHistory = (chat: DataItem[]) => {
        const persisted = updatedDataWithPlaceholder.map((entry) =>
          entry.provider_id === activeProvider
            ? {
                ...entry,
                session_id: sessionIdToUse,
                data: {
                  ...entry.data,
                  chatData: chat,
                  use_document: true,
                  has_document: true,
                  file_id: fileId,
                  file_type: fileMetaData.type,
                },
              }
            : entry,
        );
        localStorage.setItem('chat', JSON.stringify(persisted));
      };

      const { signal: streamSignal, streamSessionId } = beginStreamSession(
        chatWithPlaceholder as DataItem[],
        aiMessageIndex,
        persistFileChatHistory,
      );
      const isCurrentStreamActive = () => isStreamSessionActive(streamSessionId);

      retryContextRef.current = { sessionId: sessionIdToUse, queryText: queryText || '' };
      retryRunnerRef.current = () => {
        const ctx = retryContextRef.current;
        if (!ctx) return;

        prepareChatForRetry(ctx.sessionId, ctx.queryText);
        commitUploadedFileToChat(ctx.sessionId, file, fileId, ctx.queryText);
      };

      const updateAiMessage = (partial: Partial<DataItem>) => {
        trackStreamTokenUsage((partial as { tokenUsage?: DataItem['tokenUsage'] }).tokenUsage);
        chatWithPlaceholder = [...chatWithPlaceholder];
        chatWithPlaceholder[aiMessageIndex] = {
          ...chatWithPlaceholder[aiMessageIndex],
          ...partial,
        } as DataItem;
        if (activeStreamFinalizeRef.current) {
          activeStreamFinalizeRef.current.chat = chatWithPlaceholder as DataItem[];
        }
        dispatch({ type: 'SET_CHAT_HISTORY', payload: chatWithPlaceholder });
        // Persist during stream so refresh keeps the latest AI text (not a stuck loader placeholder)
        persistFileChatHistory(chatWithPlaceholder);
      };

      const agentCodeInterpreterEnabled = effectiveAgentToolEnabled(
        activeQueryAgent,
        'code_interpreter_enabled',
        codeInterpreterEnabled,
      );
      const agentWebSearchMode = effectiveAgentWebSearchMode(activeQueryAgent, webSearchMode);
      const agentWebSearchEnabled = agentWebSearchMode === 'on';
      const shouldEnableExcelServerAgent = isExcelFile(fileMetaData.type, fileMetaData.name);

      const isRetry = skipAddHumanOnNextSendRef.current;
      if (isRetry) {
        skipAddHumanOnNextSendRef.current = false;
      }

      // Call chat API with file_id and query
      generateNewChat2(
        queryText,
        model,
        useDocument,
        fileId,
        'Query',
        sessionIdToUse,
        agentWebSearchEnabled,
        agentCodeInterpreterEnabled,
        activeQueryAgent?.id,
        shouldEnableExcelServerAgent,
        state.thinkingEnabled,
        {
          signal: streamSignal,
          retry: isRetry,
          routingMode: routingModeForSend(),
          webSearchMode: agentWebSearchMode,
        },
        createStreamEventHandler({
          accumulatedText,
          accumulatedThinking,
          sources,
          webSearchUsed,
          webSearchQuery,
          generatedFiles,
          tokenUsage,
          toolCalls,
          mcpWarnings,
          updateAiMessage,
          aiPlaceholder,
          isStreamStopped: () => !isCurrentStreamActive(),
          onDone: () => {
            const finalChatData = [...chatWithPlaceholder];
            const finalMessage = {
              ...finalChatData[aiMessageIndex],
              isThinking: false,
              isStreaming: false,
              stream_end: true,
              message: accumulatedText.current,
              thinkingText: accumulatedThinking.current || '',
              sources: sources.current,
              webSearchUsed: webSearchUsed.current,
              webSearchQuery: webSearchQuery.current || undefined,
              generatedFiles: generatedFiles.current,
              toolCalls: toolCalls.current.length > 0 ? toolCalls.current : undefined,
              mcpWarnings: mcpWarnings.current.length > 0 ? mcpWarnings.current : undefined,
              tokenUsage: tokenUsage.current,
            };
            finalChatData[aiMessageIndex] = finalMessage as DataItem;

            // Always persist from fresh storage so we don't overwrite with a stale snapshot
            try {
              const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
                provider_id: number;
                session_id?: string;
                data: Record<string, unknown>;
              }>;
              const finalUpdatedChats = stored.map((entry) =>
                entry.provider_id === activeProvider
                  ? {
                      ...entry,
                      session_id: sessionIdToUse,
                      data: {
                        ...entry.data,
                        chatData: finalChatData,
                        use_document: true,
                        has_document: true,
                        file_id: fileId,
                        file_type: fileMetaData.type,
                      },
                    }
                  : entry,
              );
              localStorage.setItem('chat', JSON.stringify(finalUpdatedChats));
            } catch {
              persistFileChatHistory(finalChatData);
            }

            dispatch({ type: 'SET_CHAT_HISTORY', payload: finalChatData });
            dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
            clearFileUploadUiState();
            dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
            endStreamSession(streamSessionId);
            window.dispatchEvent(new CustomEvent('chatResponseComplete'));
          },
          onError: (errorMsg) => {
            dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
            endStreamSession(streamSessionId);
            setShowError(true);

            const errorResponse = {
              role: 'AI',
              message: 'Something went wrong while generating response.',
              time: new Date(),
              type: fileMetaData.type,
              stream_end: true,
              isThinking: false,
              isStreaming: false,
            };

            const errorChat = [...newChat, errorResponse];

            try {
              const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
                provider_id: number;
                session_id?: string;
                data: Record<string, unknown>;
              }>;
              const updatedErrorChats = stored.map((entry) =>
                entry.provider_id === activeProvider
                  ? {
                      ...entry,
                      session_id: sessionIdToUse,
                      data: {
                        ...entry.data,
                        chatData: errorChat,
                      },
                    }
                  : entry,
              );
              localStorage.setItem('chat', JSON.stringify(updatedErrorChats));
            } catch {
              // ignore persistence errors on failure path
            }

            dispatch({ type: 'SET_CHAT_HISTORY', payload: errorChat });
            dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
            clearFileUploadUiState();

            MakeToast('error', errorMsg);
          },
        }),
        () => {
          dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
          endStreamSession(streamSessionId);
        },
        (error: Error) => {
          dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
          endStreamSession(streamSessionId);
          setShowError(true);

          const errorResponse = {
            role: 'AI',
            message: 'Something went wrong',
            time: new Date(),
            type: fileMetaData.type,
            stream_end: true,
            isThinking: false,
            isStreaming: false,
          };

          const errorChat = [...newChat, errorResponse];

          try {
            const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
              provider_id: number;
              session_id?: string;
              data: Record<string, unknown>;
            }>;
            const updatedErrorChats = stored.map((entry) =>
              entry.provider_id === activeProvider
                ? {
                    ...entry,
                    session_id: sessionIdToUse,
                    data: {
                      ...entry.data,
                      chatData: errorChat,
                    },
                  }
                : entry,
            );
            localStorage.setItem('chat', JSON.stringify(updatedErrorChats));
          } catch {
            // ignore persistence errors on failure path
          }

          dispatch({ type: 'SET_CHAT_HISTORY', payload: errorChat });
          dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
          clearFileUploadUiState();

          MakeToast('error', error.message || 'Failed to get response');
        },
      ).catch((error) => {
        dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
        setShowError(true);

        const errorResponse = {
          role: 'AI',
          message: 'Something went wrong',
          time: new Date(),
          type: fileMetaData.type,
          stream_end: true,
          isThinking: false,
          isStreaming: false,
        };

        const errorChat = [...newChat, errorResponse];

        try {
          const stored = JSON.parse(localStorage.getItem('chat') || '[]') as Array<{
            provider_id: number;
            session_id?: string;
            data: Record<string, unknown>;
          }>;
          const updatedErrorChats = stored.map((entry) =>
            entry.provider_id === activeProvider
              ? {
                  ...entry,
                  session_id: sessionIdToUse,
                  data: {
                    ...entry.data,
                    chatData: errorChat,
                  },
                }
              : entry,
          );
          localStorage.setItem('chat', JSON.stringify(updatedErrorChats));
        } catch {
          // ignore persistence errors on failure path
        }

        dispatch({ type: 'SET_CHAT_HISTORY', payload: errorChat });
        dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
        clearFileUploadUiState();

        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to send message';
        MakeToast('error', errorMessage);
      });
    } else {
      dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });

      const updatedChats = parsedHistory.map((entry) =>
        entry.provider_id === activeProvider
          ? {
              ...entry,
              session_id: sessionIdToUse,
              data: {
                ...entry.data,
                chatData: newChat,
                use_document: true,
                has_document: true,
                file_id: fileId,
                file_type: fileMetaData.type,
              },
            }
          : entry,
      );

      localStorage.setItem('chat', JSON.stringify(updatedChats));
      dispatch({ type: 'SET_CHAT_HISTORY', payload: newChat });
      dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
      clearFileUploadUiState();
    }
  };

  // Upload file as soon as it is chosen; user can type query while upload runs
  const startImmediateFileUpload = (
    file: File,
    codeInterpreterForUpload = codeInterpreterEnabled,
  ) => {
    const requestId = fileUploadRequestIdRef.current + 1;
    fileUploadRequestIdRef.current = requestId;
    setPendingUploadedFileId(null);
    setPiiConfirmation({ show: false, piiScan: null });
    piiUploadContextRef.current = null;
    setFileUploadPending(true);

    fileUploadAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    fileUploadAbortControllerRef.current = abortController;

    const fileMessage = buildFileUploadMessageText(file.name, formatFileSize(file.size));

    const runUpload = (sessionIdToUse: string) => {
      if (requestId !== fileUploadRequestIdRef.current) return;
      pendingUploadSessionIdRef.current = sessionIdToUse;

      uploadFile(
        file,
        sessionIdToUse,
        fileMessage,
        codeInterpreterForUpload,
        abortController.signal,
      )
        .then((res) => {
          if (requestId !== fileUploadRequestIdRef.current) return;
          if (abortController.signal.aborted) return;

          if (res?.status === 200) {
            const uploadData = res.data?.data;
            const fileId = uploadData?.file_id;
            const piiScan = uploadData?.pii_scan as PiiScan | undefined;
            const totalDetected = piiScan?.total_detected ?? 0;

            if (!fileId) {
              setFileUploadPending(false);
              clearPendingFileComposerState();
              MakeToast('error', 'We were unable to upload your file. Please try again.');
              return;
            }

            if (totalDetected > 0 && piiScan) {
              setFileUploadPending(false);
              fileUploadAbortControllerRef.current = null;
              piiUploadContextRef.current = {
                sessionIdToUse,
                file,
                fileId,
              };
              setPiiConfirmation({ show: true, piiScan });
              return;
            }

            fileUploadAbortControllerRef.current = null;
            setPendingUploadedFileId(fileId);
            setFileUploadPending(false);
          } else if (res.status === 401) {
            setFileUploadPending(false);
            setLogout(true);
          } else {
            setFileUploadPending(false);
            clearPendingFileComposerState();
            MakeToast('error', 'We were unable to upload your file. Please try again.');
          }
        })
        .catch((err) => {
          if (requestId !== fileUploadRequestIdRef.current) return;
          if (
            abortController.signal.aborted ||
            err?.code === 'ERR_CANCELED' ||
            err?.name === 'CanceledError' ||
            err?.name === 'AbortError'
          ) {
            return;
          }
          setFileUploadPending(false);
          clearPendingFileComposerState();
          const reason =
            err?.response?.data?.reason ||
            err?.response?.data?.message ||
            'We were unable to upload your file. Please try again.';
          MakeToast('error', reason, 6000);
        });
    };

    const existingSessionId = currentProviderData?.session_id;
    if (existingSessionId) {
      runUpload(existingSessionId);
      return;
    }

    const modalId =
      currentProviderData?.data?.modal?.id ||
      parsedData?.find((p) => p.provider_id === activeProvider)?.data?.modal?.id ||
      0;

    CreateNewChatSession({
      model_id: modalId,
      provider_id: activeProvider,
    })
      .then((res) => {
        if (requestId !== fileUploadRequestIdRef.current) return;
        if (abortController.signal.aborted) return;
        if (res.status === 201) {
          const sessionId = res?.data?.data?.session_id;
          const updatedChat = parsedHistory.map((item) =>
            item.provider_id === activeProvider ? { ...item, session_id: sessionId } : item,
          );
          localStorage.setItem('chat', JSON.stringify(updatedChat));
          runUpload(sessionId);
        } else {
          setFileUploadPending(false);
          clearPendingFileComposerState();
          MakeToast('error', 'Session creation failed!');
        }
      })
      .catch(() => {
        if (requestId !== fileUploadRequestIdRef.current) return;
        if (abortController.signal.aborted) return;
        setFileUploadPending(false);
        clearPendingFileComposerState();
        MakeToast('error', 'Something went wrong while creating session!');
      });
  };

  const handleSendQuery = async () => {
    const retryOverride = pendingRetryQueryRef.current;
    if (retryOverride != null) {
      pendingRetryQueryRef.current = null;
    }

    if (isSelectedModelAdminBlocked) {
      MakeToast('error', ADMIN_DISABLED_MODEL_MESSAGE);
      return;
    }

    // Prevent sending while AI is responding or file is not ready
    if (!retryOverride && (aiIsResponding || isSendBlockedByFile)) {
      return;
    }

    // Store values before clearing
    const file = retryOverride != null ? null : state.pendingFile;
    const uploadedFileId = retryOverride != null ? null : pendingUploadedFileId;
    const queryText =
      retryOverride ?? (state.updateQuery === '' ? state.queryValue : state.updateQuery);

    // Clear query immediately from UI
    dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
    dispatch({ type: 'SET_UPDATE_QUERY', payload: '' });

    // Clear contentEditable
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = '<br>';
      contentEditableRef.current.classList.add('empty-content');
    }

    // File already uploaded on select — commit to chat and send query
    if (file) {
      if (!uploadedFileId) {
        return;
      }

      const sessionIdToUse =
        currentProviderData?.session_id || pendingUploadSessionIdRef.current || '';

      if (!sessionIdToUse) {
        MakeToast('error', 'Session not found. Please try uploading the file again.');
        return;
      }

      setPendingUploadedFileId(null);
      commitUploadedFileToChat(sessionIdToUse, file, uploadedFileId, queryText || undefined);
      return;
    }

    dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);

    const isFirstQuery =
      !currentProviderData ||
      !currentProviderData.data?.chatData ||
      currentProviderData.data.chatData.length === 0;

    const proceedWithQuery = (sessionIdToUse: string, queryOverride?: string) => {
      setIsContentAvailable(true);
      setGenerationUi(false);
      // Query value already cleared above
      // Keep selected agent after sending query - user must manually remove it
      if (state.showEditQueryActions) {
        dispatch({ type: 'SET_SHOW_EDIT_QUERY_ACTIONS', payload: false });
      }
      dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: true });

      const value = queryOverride ?? queryText;
      const skipAddHuman = skipAddHumanOnNextSendRef.current;
      const inPlaceRetryIndex = skipAppendAiOnRetryRef.current
        ? retryAiMessageIndexRef.current
        : null;

      if (skipAppendAiOnRetryRef.current) {
        skipAppendAiOnRetryRef.current = false;
        skipAddHumanOnNextSendRef.current = false;
        retryAiMessageIndexRef.current = null;
      } else if (skipAddHuman) {
        skipAddHumanOnNextSendRef.current = false;
      }

      const aiPlaceholder = createStreamAiPlaceholder();

      let chatWithPlaceholder: DataItem[];
      let aiMessageIndex: number;
      let chatData: DataItem[];

      if (inPlaceRetryIndex != null && inPlaceRetryIndex >= 0) {
        if (retryPreparedChatRef.current) {
          chatWithPlaceholder = [...retryPreparedChatRef.current];
        } else if (chatHistoryRef.current.length) {
          chatWithPlaceholder = [...chatHistoryRef.current];
        } else {
          chatWithPlaceholder = [...(state.chatHistory || [])];
        }

        retryPreparedChatRef.current = null;
        aiMessageIndex = inPlaceRetryIndex;
        chatData = chatWithPlaceholder.slice(0, aiMessageIndex);
      } else {
        const baseChatData = skipAddHuman
          ? [...(retryBaseChatRef.current ?? chatHistoryRef.current ?? [])]
          : [
              ...(state.chatHistory?.length
                ? state.chatHistory
                : currentProviderData?.data?.chatData || []),
            ];

        if (skipAddHuman) {
          retryBaseChatRef.current = null;
        }

        const newQuery = skipAddHuman
          ? []
          : [
              {
                role: 'Human',
                message: value,
                time: new Date(),
                type: 'Query',
              },
            ];

        chatData = [...baseChatData, ...newQuery] as DataItem[];
        aiMessageIndex = chatData.length;
        chatWithPlaceholder = [...chatData, aiPlaceholder];
      }

      dispatch({ type: 'SET_CHAT_HISTORY', payload: chatWithPlaceholder });

      retryContextRef.current = { sessionId: sessionIdToUse, queryText: value ?? '' };
      retryRunnerRef.current = () => {
        const ctx = retryContextRef.current;
        if (!ctx) return;

        prepareChatForRetry(ctx.sessionId, ctx.queryText);
        proceedWithQuery(ctx.sessionId, ctx.queryText);
      };

      let updatedData = (parsedHistory || [])?.map((entry) =>
        entry.provider_id === activeProvider
          ? {
              ...entry,
              session_id: sessionIdToUse,
              data: {
                ...entry.data,
                chatData: chatWithPlaceholder,
              },
            }
          : entry,
      );

      localStorage.setItem('chat', JSON.stringify(updatedData));

      const useDocument = currentProviderData?.data?.use_document;
      const uploadedFileType = currentProviderData?.data?.file_type;

      const model =
        (typeof currentProviderData?.data?.modal !== 'object'
          ? currentProviderData?.data?.modal
          : currentProviderData?.data?.modal?.name) || '';

      const fileId = currentProviderData?.data?.file_id;

      const persistChatHistory = (chat: DataItem[]) => {
        const persisted = updatedData.map((entry) =>
          entry.provider_id === activeProvider
            ? {
                ...entry,
                session_id: sessionIdToUse,
                data: {
                  ...entry.data,
                  chatData: chat,
                },
              }
            : entry,
        );
        localStorage.setItem('chat', JSON.stringify(persisted));
      };

      const { signal: streamSignal, streamSessionId } = beginStreamSession(
        chatWithPlaceholder,
        aiMessageIndex,
        persistChatHistory,
      );
      const isCurrentStreamActive = () => isStreamSessionActive(streamSessionId);

      let accumulatedText = '';
      let accumulatedThinking = '';
      let thinkingStartedAt = 0;
      const thinkingSeconds = () => {
        if (!thinkingStartedAt) return undefined;
        return Math.max(1, Math.round((Date.now() - thinkingStartedAt) / 1000));
      };
      let sources: DataItem['sources'] = [];
      let generatedFiles: unknown;
      let webSearchUsed = false;
      let webSearchQuery = '';
      let tokenUsage: {
        input_tokens?: number;
        reasoning_tokens?: number;
        output_tokens?: number;
      } = {};
      let toolCalls: ToolCall[] = [];
      let mcpWarnings: string[] = [];
      let currentToolCall: ToolCall | null = null;

      const updateAiMessage = (partial: Record<string, unknown>) => {
        trackStreamTokenUsage(partial.tokenUsage as DataItem['tokenUsage']);
        chatWithPlaceholder = [...chatWithPlaceholder];
        chatWithPlaceholder[aiMessageIndex] = {
          ...chatWithPlaceholder[aiMessageIndex],
          ...partial,
        } as DataItem;
        if (activeStreamFinalizeRef.current) {
          activeStreamFinalizeRef.current.chat = chatWithPlaceholder;
        }
        // Immediate UI update for real-time streaming
        dispatch({ type: 'SET_CHAT_HISTORY', payload: chatWithPlaceholder });
        persistChatHistory(chatWithPlaceholder);
      };

      const callStream = async () => {
        try {
          const agentCodeInterpreterEnabled = effectiveAgentToolEnabled(
            activeQueryAgent,
            'code_interpreter_enabled',
            codeInterpreterEnabled,
          );
          const agentWebSearchMode = effectiveAgentWebSearchMode(activeQueryAgent, webSearchMode);
          const agentWebSearchEnabled = agentWebSearchMode === 'on';
          const shouldEnableExcelServerAgent =
            Boolean(useDocument) && isExcelFile(uploadedFileType);

          await generateNewChat2(
            value,
            model,
            useDocument,
            useDocument === true ? fileId : undefined,
            'Query',
            sessionIdToUse,
            agentWebSearchEnabled,
            agentCodeInterpreterEnabled,
            activeQueryAgent?.id,
            shouldEnableExcelServerAgent,
            state.thinkingEnabled,
            {
              signal: streamSignal,
              retry: skipAddHuman,
              routingMode: routingModeForSend(),
              webSearchMode: agentWebSearchMode,
            },
            (event: StreamEvent) => {
              const sessionMatches =
                activeStreamFinalizeRef.current?.streamSessionId === streamSessionId;

              if (event?.type === 'token_usage' && sessionMatches) {
                const currentInput =
                  typeof tokenUsage?.input_tokens === 'number' ? tokenUsage.input_tokens : 0;
                const currentReasoning =
                  typeof tokenUsage?.reasoning_tokens === 'number'
                    ? tokenUsage.reasoning_tokens
                    : 0;
                const currentOutput =
                  typeof tokenUsage?.output_tokens === 'number' ? tokenUsage.output_tokens : 0;
                tokenUsage = {
                  input_tokens: (event.input_tokens as number) ?? currentInput,
                  reasoning_tokens: (event.reasoning_tokens as number) ?? currentReasoning,
                  output_tokens: (event.output_tokens as number) ?? currentOutput,
                };
                updateAiMessage({ tokenUsage });
                if (streamStopRequestedRef.current) {
                  completeStopAfterTokens();
                }
                return;
              }

              if (!isCurrentStreamActive() || streamStopRequestedRef.current) return;

              try {
                switch (event?.type) {
                  case 'thinking':
                    if (event.type === 'thinking') {
                      if (!thinkingStartedAt) thinkingStartedAt = Date.now();
                      accumulatedThinking += event.delta;
                      updateAiMessage({
                        isThinking: true,
                        isStreaming: false,
                        thinkingText: accumulatedThinking,
                        thinkingDurationSec: thinkingSeconds(),
                        message: '',
                      });
                    }
                    break;

                  case 'output':
                    if (event.type === 'output') {
                      accumulatedText += event.delta;
                      updateAiMessage({
                        isThinking: false,
                        isStreaming: true,
                        message: accumulatedText,
                        thinkingText: accumulatedThinking || '',
                        thinkingDurationSec: thinkingSeconds(),
                      });
                    }
                    break;

                  case 'generated_files':
                    generatedFiles = event.generated_files;
                    updateAiMessage({ generatedFiles });
                    break;

                  case 'sources':
                    sources = mergeWebSearchSources(
                      sources,
                      Array.isArray(event.sources) ? event.sources : undefined,
                    );
                    updateAiMessage({ sources });
                    break;

                  case 'web_search_started':
                    webSearchQuery = typeof event.query === 'string' ? event.query : '';
                    updateAiMessage({ webSearchQuery });
                    break;

                  case 'web_search_used':
                    webSearchUsed = !!event.webSearchUsed;
                    updateAiMessage({ webSearchUsed });
                    break;

                  case 'routing_decision':
                    if (event.type === 'routing_decision' && event.model_name) {
                      updateAiMessage({ routedModelName: String(event.model_name) });
                      window.dispatchEvent(
                        new CustomEvent('grigo-routing-model', { detail: event.model_name }),
                      );
                      window.dispatchEvent(
                        new CustomEvent('grigo-routing-detail', {
                          detail: {
                            model_name: event.model_name,
                            routing_band: event.resolved_routing_band ?? event.routing_band,
                            target_routing_band: event.target_routing_band,
                            resolved_routing_band: event.resolved_routing_band,
                            routing_score: event.routing_score,
                            logical_model: event.logical_model,
                            selection_reason: event.selection_reason,
                            pick_tier: event.pick_tier,
                          },
                        }),
                      );
                    }
                    break;

                  case 'mcp_tool_result': {
                    const resultEvent = event as {
                      tool_name?: string;
                      tool_id?: string;
                      mcp_id?: number;
                      result?: unknown;
                      error?: unknown;
                      arguments?: unknown;
                    };
                    if (resultEvent.tool_name) {
                      toolCalls = patchToolCallResult(toolCalls, {
                        tool_name: resultEvent.tool_name,
                        tool_id: resultEvent.tool_id,
                        mcp_id: resultEvent.mcp_id,
                        result: resultEvent.result,
                        error: resultEvent.error,
                        arguments: resultEvent.arguments,
                      });
                      updateAiMessage({ toolCalls });
                      if (resultEvent.error) {
                        handleMcpToolResultFailure(resultEvent).catch(() => undefined);
                      }
                    }
                    break;
                  }

                  case 'mcp_connection_failed': {
                    const failureEvent = event as {
                      message?: string;
                      mcp_id?: number;
                    };
                    handleMcpConnectionFailed(failureEvent).catch(() => undefined);
                    break;
                  }

                  case 'mcp_call':
                  case 'function_call_started':
                  case 'tool_call_started': {
                    const toolName =
                      ('tool_name' in event &&
                        typeof event.tool_name === 'string' &&
                        event.tool_name) ||
                      ('call_id' in event && typeof event.call_id === 'string' && event.call_id);
                    if (toolName) {
                      currentToolCall = {
                        tool_name: toolName,
                        message: '',
                        id: 'id' in event && typeof event.id === 'string' ? event.id : undefined,
                        mcp_id:
                          'mcp_id' in event && typeof event.mcp_id === 'number'
                            ? event.mcp_id
                            : undefined,
                        has_widget: 'has_widget' in event ? !!event.has_widget : undefined,
                        ui_resource_uri:
                          'ui_resource_uri' in event && typeof event.ui_resource_uri === 'string'
                            ? event.ui_resource_uri
                            : undefined,
                        is_loading: true,
                      };
                      const updatedToolCalls = [...toolCalls];
                      const existingIndex = updatedToolCalls.findIndex(
                        (tc) => tc.id === currentToolCall?.id && tc.tool_name === toolName,
                      );
                      if (existingIndex >= 0) {
                        updatedToolCalls[existingIndex] = {
                          ...updatedToolCalls[existingIndex],
                          ...currentToolCall,
                          message: updatedToolCalls[existingIndex].message || '',
                          widget_arguments: updatedToolCalls[existingIndex].widget_arguments,
                          widget_result: updatedToolCalls[existingIndex].widget_result,
                          has_widget:
                            currentToolCall.has_widget ??
                            updatedToolCalls[existingIndex].has_widget,
                          mcp_id: currentToolCall.mcp_id ?? updatedToolCalls[existingIndex].mcp_id,
                          ui_resource_uri:
                            currentToolCall.ui_resource_uri ??
                            updatedToolCalls[existingIndex].ui_resource_uri,
                        };
                      } else {
                        updatedToolCalls.push({ ...currentToolCall });
                      }
                      toolCalls = updatedToolCalls;
                      updateAiMessage({ toolCalls: updatedToolCalls });
                    }
                    break;
                  }

                  case 'mcp_widget_result': {
                    const widgetToolName =
                      'tool_name' in event && typeof event.tool_name === 'string'
                        ? event.tool_name
                        : null;
                    if (widgetToolName) {
                      const widgetToolId =
                        'id' in event && typeof event.id === 'string' ? event.id : undefined;
                      toolCalls = patchToolCallResult(toolCalls, {
                        tool_name: widgetToolName,
                        tool_id: widgetToolId,
                        mcp_id:
                          'mcp_id' in event && typeof event.mcp_id === 'number'
                            ? event.mcp_id
                            : undefined,
                        result: 'result' in event ? event.result : undefined,
                        arguments: 'arguments' in event ? event.arguments : undefined,
                        mergeResult: true,
                        has_widget: true,
                        ui_resource_uri:
                          'resource_uri' in event && typeof event.resource_uri === 'string'
                            ? event.resource_uri
                            : undefined,
                      });
                      currentToolCall =
                        toolCalls.find(
                          (tc) =>
                            tc.tool_name === widgetToolName &&
                            (!widgetToolId || tc.id === widgetToolId),
                        ) ?? null;
                      updateAiMessage({ toolCalls });
                    }
                    break;
                  }

                  case 'function_call_code':
                  case 'tool_code':
                  case 'tool_call_code': {
                    // Handle delta - can be string or object
                    if (currentToolCall && 'delta' in event && event.delta) {
                      let deltaContent = '';
                      if (typeof event.delta === 'string') {
                        deltaContent = event.delta;
                      } else if (typeof event.delta === 'object') {
                        // Stringify the entire delta object as JSON
                        deltaContent = JSON.stringify(event.delta, null, 2);
                      }

                      // If no current tool call exists but we have call_id, create one
                      if (!currentToolCall) {
                        const newToolName =
                          ('tool_name' in event &&
                            typeof event.tool_name === 'string' &&
                            event.tool_name) ||
                          ('call_id' in event &&
                            typeof event.call_id === 'string' &&
                            event.call_id) ||
                          'unknown';
                        currentToolCall = {
                          tool_name: newToolName,
                          message: deltaContent,
                          id: 'id' in event && typeof event.id === 'string' ? event.id : undefined,
                        };
                      } else {
                        currentToolCall.message += deltaContent;
                      }

                      // Update tool calls array with current tool call
                      const updatedToolCalls = [...toolCalls];
                      const existingIndex = updatedToolCalls.findIndex(
                        (tc) =>
                          tc.id === currentToolCall?.id &&
                          tc.tool_name === currentToolCall?.tool_name,
                      );
                      if (existingIndex >= 0) {
                        updatedToolCalls[existingIndex] = { ...currentToolCall };
                      } else {
                        updatedToolCalls.push({ ...currentToolCall });
                      }
                      toolCalls = updatedToolCalls;
                      updateAiMessage({ toolCalls: updatedToolCalls });
                    }
                    break;
                  }

                  case 'tool_code_done':
                  case 'tool_completed':
                    if (currentToolCall) {
                      const updatedToolCalls = [...toolCalls];
                      const existingIndex = updatedToolCalls.findIndex(
                        (tc) =>
                          tc.id === currentToolCall?.id &&
                          tc.tool_name === currentToolCall?.tool_name,
                      );
                      if (existingIndex >= 0) {
                        updatedToolCalls[existingIndex] = {
                          ...currentToolCall,
                          is_loading: false,
                        };
                      } else {
                        updatedToolCalls.push({ ...currentToolCall, is_loading: false });
                      }
                      toolCalls = finalizeToolCalls(updatedToolCalls);
                      updateAiMessage({ toolCalls });
                      currentToolCall = null;
                    } else if (toolCalls.length > 0) {
                      toolCalls = finalizeToolCalls(toolCalls);
                      updateAiMessage({ toolCalls });
                    }
                    break;

                  case 'mcp_warning': {
                    const warning =
                      'message' in event && typeof event.message === 'string' ? event.message : '';
                    if (warning) {
                      if (isMcpConnectivityMessage(warning)) {
                        showMcpFailureInSidePanel({ message: warning }).catch(() => undefined);
                      } else {
                        handleMcpStreamNotice(warning);
                        mcpWarnings = appendMcpWarning(mcpWarnings, warning);
                        updateAiMessage({
                          mcpWarnings: mcpWarnings.length > 0 ? mcpWarnings : undefined,
                        });
                      }
                    }
                    break;
                  }

                  case 'error': {
                    const errorMsg = (event?.error as string) || 'Unknown stream error';
                    if (isMcpConnectivityMessage(errorMsg)) {
                      showMcpFailureInSidePanel({ message: errorMsg }).catch(() => undefined);
                      updateAiMessage({
                        isThinking: false,
                        isStreaming: false,
                        stream_end: true,
                        message:
                          accumulatedText ||
                          'I could not reach one or more MCP servers. Your question was still processed without those tools — check MCP settings and reconnect if needed.',
                      });
                      dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
                      endStreamSession(streamSessionId);
                    } else {
                      MakeToast('error', errorMsg);
                    }
                    break;
                  }

                  case 'done':
                    // Finalize any remaining tool call
                    if (currentToolCall) {
                      const updatedToolCalls = [...toolCalls];
                      const existingIndex = updatedToolCalls.findIndex(
                        (tc) =>
                          tc.id === currentToolCall?.id &&
                          tc.tool_name === currentToolCall?.tool_name,
                      );
                      if (existingIndex >= 0) {
                        updatedToolCalls[existingIndex] = { ...currentToolCall };
                      } else {
                        updatedToolCalls.push({ ...currentToolCall });
                      }
                      toolCalls = updatedToolCalls;
                      currentToolCall = null;
                    }
                    updateAiMessage({
                      message: accumulatedText,
                      isThinking: false,
                      isStreaming: false,
                      generationStopped: false,
                      stream_end: true,
                      thinkingText: accumulatedThinking || '',
                      thinkingDurationSec: thinkingSeconds(),
                      sources,
                      webSearchUsed,
                      webSearchQuery: webSearchQuery || undefined,
                      generatedFiles,
                      toolCalls,
                      mcpWarnings: mcpWarnings.length > 0 ? mcpWarnings : undefined,
                      tokenUsage,
                    });
                    updatedData = updatedData.map((entry) =>
                      entry.provider_id === activeProvider
                        ? {
                            ...entry,
                            session_id: sessionIdToUse,
                            data: { ...entry.data, chatData: chatWithPlaceholder },
                          }
                        : entry,
                    );
                    localStorage.setItem('chat', JSON.stringify(updatedData));
                    dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
                    endStreamSession(streamSessionId);
                    // Dispatch custom event to notify header to refresh budget
                    window.dispatchEvent(new CustomEvent('chatResponseComplete'));
                    break;

                  default:
                    break;
                }
              } catch (innerErr) {
                if (!isCurrentStreamActive()) return;
                MakeToast('error', 'Something went wrong during streaming.');
                dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
                endStreamSession(streamSessionId);
              }
            },
            () => {
              if (streamStopRequestedRef.current) {
                completeStopAfterTokens();
                return;
              }
              if (!isCurrentStreamActive()) return;
              dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
              endStreamSession(streamSessionId);
            },
            (error: StreamError) => {
              if (!isCurrentStreamActive()) return;
              dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
              endStreamSession(streamSessionId);
              updateAiMessage({
                isThinking: false,
                isStreaming: false,
                message: 'Something went wrong while generating response.',
              });
              const errorUpdatedChat = chatWithPlaceholder.map((msg, idx) =>
                idx === aiMessageIndex
                  ? {
                      ...msg,
                      isThinking: false,
                      isStreaming: false,
                      message: 'Something went wrong while generating response.',
                    }
                  : msg,
              );

              updatedData = updatedData.map((entry) =>
                entry.provider_id === activeProvider
                  ? {
                      ...entry,
                      session_id: sessionIdToUse,
                      data: {
                        ...entry.data,
                        chatData: errorUpdatedChat,
                      },
                    }
                  : entry,
              );

              localStorage.setItem('chat', JSON.stringify(updatedData));

              MakeToast(
                'error',
                error?.response?.data?.message ||
                  error?.message ||
                  'Something went wrong while generating response.',
              );
            },
          );
        } catch (outerErr) {
          if (!isCurrentStreamActive()) return;
          // Any unhandled errors — they used to trigger the red screen
          MakeToast('error', 'Unexpected server error occurred.');
          dispatch({ type: 'SET_NEW_RESPONSE_LOADER', payload: false });
          endStreamSession(streamSessionId);
        }
      };

      callStream();
    };

    if (isFirstQuery && !currentProviderData?.session_id) {
      const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
      const providerObj = existingChat.find(
        (item: { provider_id: number }) => item.provider_id === activeProvider,
      );
      const modalId = providerObj?.data?.modal?.id;

      CreateNewChatSession({
        model_id: modalId,
        provider_id: activeProvider || 0,
      })
        .then((res) => {
          if (res.status === 201) {
            const sessionId = res?.data?.data?.session_id;
            const updatedChat = existingChat.map((item: { provider_id: number }) => {
              if (item.provider_id === activeProvider) {
                return {
                  ...item,
                  session_id: sessionId,
                };
              }
              return item;
            });

            localStorage.setItem('chat', JSON.stringify(updatedChat));

            const refreshed = JSON.parse(localStorage.getItem('chat') || '[]');
            parsedHistory = refreshed;
            currentProviderData = refreshed.find(
              (entry: { provider_id: number }) => entry.provider_id === activeProvider,
            );
            onClose();
            proceedWithQuery(sessionId);
          } else {
            MakeToast('error', 'Session creation failed!');
          }
        })
        .catch((error: ApiError) => {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            'Something went wrong while creating session!';
          MakeToast('error', msg);
        });
    } else if (currentProviderData?.session_id) {
      proceedWithQuery(currentProviderData.session_id);
    } else {
      MakeToast('error', 'Session ID is missing. Please try again.');
    }
  };

  handleSendQueryRef.current = handleSendQuery;

  const mentionQueryLower = mentionQuery.trim().toLowerCase();

  // Agents for active provider, narrowed by name or description as user types after @
  const filteredAgents = agents.filter((agent) => {
    if (!mentionQueryLower) return true;
    const name = (agent.agent_name ?? '').toLowerCase();
    const desc = (agent.description ?? '').toLowerCase();
    return name.includes(mentionQueryLower) || desc.includes(mentionQueryLower);
  });

  useEffect(() => {
    if (!showAgentMention) return;
    setSelectedMentionIndex((prev) =>
      filteredAgents.length === 0 ? 0 : Math.min(prev, filteredAgents.length - 1),
    );
  }, [mentionQuery, activeProvider, showAgentMention, filteredAgents.length]);

  // Handle agent selection - replace previous agent if one exists
  const handleAgentSelect = (agent: Agent) => {
    if (mentionStartIndex === -1) return;
    if (agent.provider_id !== activeProvider) return;
    const contentEditable = contentEditableRef?.current;
    if (!contentEditable) return;

    // Replace previous agent with new one (only one agent at a time)
    setSelectedQueryAgent(agent);

    // Remove @ and query text from the input
    const currentValue = state.queryValue || '';
    const textBeforeMention = currentValue.substring(0, mentionStartIndex);
    const textAfterMention = currentValue.substring(mentionStartIndex + 1 + mentionQuery.length);
    const newValue = `${textBeforeMention}${textAfterMention}`;

    dispatch({ type: 'SET_QUERY_VALUE', payload: newValue });
    setShowAgentMention(false);
    setMentionQuery('');
    setMentionStartIndex(-1);
    setSelectedMentionIndex(0);

    // Update contentEditable to remove the @ mention
    if (contentEditableRef.current) {
      const formatted = formatQueryWithMentions(newValue);
      contentEditableRef.current.innerHTML = formatted || '<br>';

      // Set cursor position after removing @ mention
      setTimeout(() => {
        if (contentEditableRef.current) {
          const newCursorPos = textBeforeMention.length;
          const range = document.createRange();
          const selection = window.getSelection();

          if (selection && contentEditableRef.current.childNodes.length > 0) {
            try {
              let charCount = 0;
              const walker = document.createTreeWalker(
                contentEditableRef.current,
                NodeFilter.SHOW_TEXT,
                null,
              );

              let node = walker.nextNode();
              while (node) {
                const nodeLength = node.textContent?.length || 0;
                if (charCount + nodeLength >= newCursorPos) {
                  range.setStart(node, newCursorPos - charCount);
                  range.setEnd(node, newCursorPos - charCount);
                  selection.removeAllRanges();
                  selection.addRange(range);
                  break;
                }
                charCount += nodeLength;
                node = walker.nextNode();
              }

              contentEditableRef.current.focus();
            } catch (err) {
              if (contentEditableRef.current) {
                contentEditableRef.current.focus();
              }
            }
          }
        }
      }, 0);
    }
  };

  // Handle removing selected agent
  const handleRemoveAgent = () => {
    setSelectedQueryAgent(null);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (showLlmSetupBanner) return;
    // Prevent all keyboard actions if AI is responding
    if (aiIsResponding) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
      return;
    }

    // Mention dropdown: navigation when there are matches; Escape always closes
    if (showAgentMention) {
      if (filteredAgents.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedMentionIndex((prev) => (prev < filteredAgents.length - 1 ? prev + 1 : prev));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const agent = filteredAgents[selectedMentionIndex];
          if (agent?.provider_id === activeProvider) {
            handleAgentSelect(agent);
          }
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowAgentMention(false);
        setMentionQuery('');
        setMentionStartIndex(-1);
        setSelectedMentionIndex(0);
        return;
      }
    }

    // Enter = send, Shift+Enter = new line (contentEditable default)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isSelectedModelAdminBlocked) {
        MakeToast('error', ADMIN_DISABLED_MODEL_MESSAGE);
        return;
      }
      const canSendWithFile = Boolean(state.pendingFile) && Boolean(pendingUploadedFileId);
      if ((state.queryValue !== '' || canSendWithFile) && !isSendBlockedByFile) {
        handleSendQuery();
      }
    }
  };

  const handleUploadIcon = () => {
    fileInputRef?.current?.click();
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return '🖼️';
    }
    if (fileType === 'application/pdf') {
      return '📄';
    }
    if (
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType === 'text/csv'
    ) {
      return '📊';
    }
    if (
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return '📝';
    }
    if (fileType === 'text/plain') {
      return '📄';
    }
    return '📎';
  };

  const persistCodeInterpreterEnabled = (isChecked: boolean): boolean => {
    if (isChecked) {
      const canEnable = ensureCompatibleModel(
        {
          thinkingEnabled: Boolean(state.thinkingEnabled),
          webSearchEnabled: Boolean(webSearchEnabled),
          codeInterpreterEnabled: true,
          mcpActive: mcpData.some((m) => m.status === 'active'),
        },
        'code interpreter',
      );
      if (!canEnable) {
        return false;
      }
    }
    const updatedChat = parsedHistory.map((entry: { provider_id: number; data: object }) =>
      entry.provider_id === activeProvider
        ? {
            ...entry,
            data: {
              ...entry.data,
              codeInterpreterEnabled: isChecked,
            },
          }
        : entry,
    );
    localStorage.setItem('chat', JSON.stringify(updatedChat));
    dispatch({ type: 'SET_CODE_INTERPRETER_ENABLED', payload: isChecked });
    return true;
  };

  // Shared validation and state update for file (used by both file input and drag-and-drop)
  const processAndSetPendingFile = (file: File | null) => {
    if (!file) return;
    if (aiIsResponding || isFileUploadInProgress || isPiiConfirmationOpen) return;

    if (file.name.length > 90) {
      MakeToast('error', 'File name is too long. Please rename the file and try again.');
      return;
    }

    const allowedTypes = [
      'text/plain',
      'text/csv',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      MakeToast(
        'error',
        'Only .txt, .csv, .png, .jpg, .jpeg, .webp, .pdf, .xlsx, .doc, .docx files are allowed!',
      );
      return;
    }

    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      MakeToast('error', 'File size must be less than 10MB. Please choose a smaller file.');
      return;
    }

    dispatch({ type: 'SET_PENDING_FILE', payload: file } as PendingFileAction);
    let codeInterpreterForUpload = Boolean(codeInterpreterEnabled);
    if (needsCodeInterpreterFile(file.type, file.name) && !codeInterpreterForUpload) {
      codeInterpreterForUpload = persistCodeInterpreterEnabled(true);
    }
    startImmediateFileUpload(file, codeInterpreterForUpload);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    processAndSetPendingFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      aiIsResponding ||
      isFileUploadInProgress ||
      isPiiConfirmationOpen ||
      RagOptions?.length === 0
    )
      return;
    const file = e.dataTransfer.files?.[0];
    processAndSetPendingFile(file ?? null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const persistWebSearchMode = (mode: WebSearchMode): boolean => {
    if (mode === 'on') {
      const canEnable = ensureCompatibleModel(
        {
          thinkingEnabled: Boolean(state.thinkingEnabled),
          webSearchEnabled: true,
          codeInterpreterEnabled: Boolean(codeInterpreterEnabled),
          mcpActive: mcpData.some((m) => m.status === 'active'),
        },
        'web search',
      );
      if (!canEnable) {
        return false;
      }
    }

    const flags = persistWebSearchFlags(mode);
    const updatedChat = parsedHistory.map((entry: { provider_id: number; data: object }) =>
      entry.provider_id === activeProvider
        ? {
            ...entry,
            data: {
              ...entry.data,
              ...flags,
            },
          }
        : entry,
    );

    localStorage.setItem('chat', JSON.stringify(updatedChat));
    dispatch({ type: 'SET_WEB_SEARCH_MODE', payload: mode });
    return true;
  };

  const handleWebSearchToggle = (e: InputChangeEvent) => {
    persistWebSearchMode(e.target.checked ? 'on' : 'auto');
  };

  const handleChatInterpreterToggle = (e: InputChangeEvent) => {
    persistCodeInterpreterEnabled(e.target.checked);
  };

  const handleThinkingToggle = (isChecked: boolean) => {
    if (isChecked) {
      const canEnable = ensureCompatibleModel(
        {
          thinkingEnabled: true,
          webSearchEnabled: Boolean(webSearchEnabled),
          codeInterpreterEnabled: Boolean(codeInterpreterEnabled),
          mcpActive: mcpData.some((m) => m.status === 'active'),
        },
        'thinking',
      );
      if (!canEnable) {
        return;
      }
    }
    const updatedChat = parsedHistory.map((entry: { provider_id: number; data: object }) =>
      entry.provider_id === activeProvider
        ? {
            ...entry,
            data: {
              ...entry.data,
              thinkingEnabled: isChecked,
            },
          }
        : entry,
    );

    localStorage.setItem('chat', JSON.stringify(updatedChat));
    dispatch({ type: 'SET_THINKING_ENABLED', payload: isChecked });
  };

  const handleUseDocument = () => {
    const updatedData = parsedHistory.map((entry) =>
      entry.provider_id === activeProvider
        ? {
            ...entry,
            data: {
              ...entry.data,
              use_document: !entry.data.use_document,
            },
          }
        : entry,
    );

    localStorage.setItem('chat', JSON.stringify(updatedData));
    dispatch({
      type: 'SET_USE_DOCUMENT_ONLY',
      payload: !currentProviderData?.data?.use_document,
    });
    MakeToast(
      'success',
      `Uploaded document will ${
        currentProviderData?.data?.use_document ? 'not' : ''
      } be used as reference for queries!`,
    );
  };

  const openAddMcpModal = () => {
    setReAuthOpenModa(EMPTY_MCP_RE_AUTH);
    setMcpPrefillConfig(null);
    setShowTunePopup(false);
    setShowTuneModal(true);
  };

  const renderMcpToggle = (mcp: McpServer) => (
    <span className="mcpSwitch">
      <input
        type="checkbox"
        checked={mcp.status === 'active'}
        onChange={() => handleUpdateMcpStatus(mcp.id, mcp.status)}
      />
      <span className="mcpSlider round" />
    </span>
  );

  const renderMcpReconnectButton = (mcp: McpServer) => {
    const isConfigOpen =
      showTuneModal && mcpPrefillConfig?.mcpName === mcp.name && mcpPrefillConfig?.url === mcp.url;

    return (
      <button
        type="button"
        className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => openMcpConnectModal(mcp)}
        disabled={isConfigOpen}
        aria-label="Reconnect MCP"
      >
        <StyledTooltip title="Reconnect">
          <ReplayIcon className="text-gray-600" sx={{ width: '17px' }} />
        </StyledTooltip>
      </button>
    );
  };

  const renderMcpPrimaryAction = (mcp: McpServer) =>
    mcpShowsReconnectActions(mcp, mcpPanelMessages[mcp.id])
      ? renderMcpReconnectButton(mcp)
      : renderMcpToggle(mcp);

  const handlePiiUploadConfirm = () => {
    const context = piiUploadContextRef.current;

    setPiiConfirmation({ show: false, piiScan: null });
    piiUploadContextRef.current = null;

    if (context?.fileId) {
      setPendingUploadedFileId(context.fileId);
      pendingUploadSessionIdRef.current = context.sessionIdToUse;
      dispatch({ type: 'SET_PENDING_FILE', payload: context.file } as PendingFileAction);
      setFileUploadPending(false);
    }
  };

  const handlePiiUploadCancel = () => {
    piiUploadContextRef.current = null;
    setPiiConfirmation({ show: false, piiScan: null });
    clearPendingFileComposerState();
    dispatch({ type: 'SET_PENDING_FILE', payload: null } as PendingFileAction);
    setPendingUploadedFileId(null);
    pendingUploadSessionIdRef.current = null;
  };

  const closeTuneModal = () => {
    const reconnectMcpId = mcpPrefillConfig?.mcpId;
    setShowTuneModal(false);
    setReAuthOpenModa(EMPTY_MCP_RE_AUTH);
    setMcpPrefillConfig(null);
    invalidateMcpWidgetHtmlCache();
    if (reconnectMcpId != null) {
      clearMcpPanelMessage(reconnectMcpId);
    }
    allMcpApi().then(async (mcps) => {
      mcps.forEach((mcp) => {
        if (mcp.status === 'active') {
          clearMcpPanelMessage(mcp.id);
        }
      });
      const needsRefresh = mcps.filter(
        (mcp) => mcp.status === 'active' && (!Array.isArray(mcp.tools) || mcp.tools.length === 0),
      );
      if (needsRefresh.length === 0) {
        return;
      }
      await Promise.all(
        needsRefresh.map(async (mcp) => {
          try {
            await RefreshMcpToolsService(mcp.id);
          } catch {
            /* refresh may fail if server still unreachable */
          }
        }),
      );
      await allMcpApi();
    });
  };

  const handleDeleteAgent = async () => {
    if (!deleteConfirmation.agentId) return;
    try {
      const res = await deleteAgent(deleteConfirmation.agentId);
      if (res.status === 200) {
        makeToast('success', 'Agent deleted successfully');
        fetchAgents();
        setDeleteConfirmation({ show: false, agentId: null, agentName: '' });
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError?.response?.data?.status === 401) {
        setLogout(true);
      } else {
        makeToast('error', 'Failed to delete agent');
      }
    }
  };

  const showDeleteConfirmation = (agentId: number, agentName: string) => {
    setDeleteConfirmation({ show: true, agentId, agentName });
  };

  const handleEditAgent = async (agentId: number) => {
    try {
      const res = await getAgentById(agentId);
      if (res.status === 200) {
        const agentData = res.data?.data;
        setEditingAgent(agentData);
        setShowChatAgentModal(true);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError?.response?.data?.status === 401) {
        setLogout(true);
      } else {
        makeToast('error', 'Failed to fetch agent details');
      }
    }
  };

  const selectedMenuAgent = agents.find((agent) => agent.id === selectedAgent) ?? null;
  const mentionedAgent =
    activeQueryAgent && activeQueryAgent.id !== selectedMenuAgent?.id ? activeQueryAgent : null;
  const { thinkingAvailable, toolsAvailable } = assignedFeatureAvailability(llmPermissions);
  const showThinkingOption = !llmPermissionsLoaded || thinkingAvailable;
  const showMcpOptions = !llmPermissionsLoaded || toolsAvailable;
  const hasActiveComposerTools = Boolean(
    webSearchEnabled ||
      codeInterpreterEnabled ||
      state.thinkingEnabled ||
      selectedMenuAgent ||
      activeQueryAgent,
  );

  return (
    <>
      <div className="chat-composer-dock flex flex-col w-[calc(100%-0rem)] gap-2 pt-2 pb-0 bg-transparent border-t border-transparent">
        {/* Input with border and send button - overflow-visible so @ agent dropdown is not clipped */}
        <ChatThreadLayout className="overflow-visible py-0 mt-2">
          <div
            className="relative flex flex-col w-full gap-2 overflow-visible"
            onDragOver={handleDragOver}
            onDrop={handleFileDrop}
          >
            {/* File Preview Container */}
            {showPendingFileInQuery && state.pendingFile && (
              <div className="flex items-center justify-start gap-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="text-2xl flex-shrink-0">
                    {getFileIcon(state.pendingFile.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{state.pendingFile.name}</div>
                    <div className="text-xs text-gray-500">
                      {formatFileSize(state.pendingFile.size)} • {state.pendingFile.type}
                    </div>
                  </div>
                </div>
                {isFileUploadInProgress && (
                  <CircularProgress size={20} sx={{ color: '#0A8BC7', flexShrink: 0 }} />
                )}
                <button
                  type="button"
                  onClick={() => {
                    clearPendingFileComposerState();
                    setPiiConfirmation({ show: false, piiScan: null });
                    piiUploadContextRef.current = null;
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                  aria-label="Remove file"
                >
                  <img src={CrossIcon} alt="Remove" className="w-4 h-4" />
                </button>
              </div>
            )}

            <div
              className={`relative flex flex-wrap items-center border border-[#E4E4E7] bg-white w-full mb-1 overflow-visible chat-composer-unified ${
                hasActiveComposerTools ? 'rounded-[1.75rem]' : 'rounded-full'
              }`}
            >
              {hasActiveComposerTools ? (
                <div className="basis-full flex flex-wrap items-center gap-1.5 px-3 pt-2.5 pb-0.5">
                  {webSearchEnabled ? (
                    <span className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full bg-[#e0f1f8] text-[#0A8BC7] text-xs font-medium">
                      <Globe size={12} />
                      Web Search
                      <button
                        type="button"
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#0A8BC7]/10"
                        aria-label="Use Auto Web Search"
                        onClick={() => persistWebSearchMode('auto')}
                      >
                        <img src={CrossIcon} alt="" className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ) : null}
                  {codeInterpreterEnabled ? (
                    <span className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full bg-[#e0f1f8] text-[#0A8BC7] text-xs font-medium">
                      <Code size={12} />
                      Code Interpreter
                      <button
                        type="button"
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#0A8BC7]/10"
                        aria-label="Turn off Code Interpreter"
                        onClick={() =>
                          handleChatInterpreterToggle({
                            target: { checked: false },
                          } as InputChangeEvent)
                        }
                      >
                        <img src={CrossIcon} alt="" className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ) : null}
                  {state.thinkingEnabled ? (
                    <span className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full bg-[#e0f1f8] text-[#0A8BC7] text-xs font-medium">
                      <img src={BrainIcon} alt="" className="w-3 h-3" />
                      Thinking
                      <button
                        type="button"
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#0A8BC7]/10"
                        aria-label="Turn off Thinking"
                        onClick={() => handleThinkingToggle(false)}
                      >
                        <img src={CrossIcon} alt="" className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ) : null}
                  {selectedMenuAgent ? (
                    <span className="inline-flex items-center gap-1 h-7 max-w-full pl-2 pr-1 rounded-full bg-[#e0f1f8] text-[#0A8BC7] text-xs font-medium">
                      <img src={ChatAgentIcon} alt="" className="w-3 h-3" />
                      <span className="truncate max-w-[140px]">{selectedMenuAgent.agent_name}</span>
                      <button
                        type="button"
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#0A8BC7]/10"
                        aria-label={`Remove ${selectedMenuAgent.agent_name}`}
                        onClick={() => {
                          setSelectedAgent(null);
                          if (activeQueryAgent?.id === selectedMenuAgent.id) {
                            handleRemoveAgent();
                          }
                        }}
                      >
                        <img src={CrossIcon} alt="" className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ) : null}
                  {mentionedAgent ? (
                    <span className="inline-flex items-center gap-1 h-7 max-w-full pl-2 pr-1 rounded-full bg-[#e0f1f8] text-[#0A8BC7] text-xs font-medium">
                      <img src={ChatAgentIcon} alt="" className="w-3 h-3" />
                      <span className="truncate max-w-[140px]">{mentionedAgent.agent_name}</span>
                      <button
                        type="button"
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#0A8BC7]/10"
                        aria-label={`Remove ${mentionedAgent.agent_name}`}
                        onClick={handleRemoveAgent}
                      >
                        <img src={CrossIcon} alt="" className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="relative min-w-0 flex-1 order-2">
                <ChatComposerShell
                  className="border-0 rounded-none bg-transparent"
                  disabled={aiIsResponding || RagOptions?.length === 0 || showLlmSetupBanner}
                  trailing={
                    <>
                      {setActiveProvider ? (
                        <ModelTabs
                          placement="toolbar"
                          activeTab={activeProvider}
                          setActiveTab={setActiveProvider}
                          setActiveProvider={setActiveProvider}
                          dispatch={dispatch}
                          activeProviderId={activeProvider}
                          state={state}
                          setIsContentAvailable={setIsContentAvailable}
                          onClose={onClose}
                          llmPermissions={llmPermissions}
                          llmPermissionsLoaded={llmPermissionsLoaded}
                        />
                      ) : null}
                      {isGenerationActive && onStopGeneration ? (
                        <StyledTooltip title="Stop Generation">
                          <button
                            type="button"
                            onClick={onStopGeneration}
                            className="flex items-center justify-center w-9 h-9 bg-[#C40018] text-white hover:bg-[#A30014] rounded-full cursor-pointer shadow-[0_0_14px_rgba(255,30,40,0.85)] hover:shadow-[0_0_18px_rgba(255,20,30,1)] transition-all"
                            aria-label="Stop Generation"
                          >
                            <Square size={16} fill="currentColor" />
                          </button>
                        </StyledTooltip>
                      ) : null}
                      {!isGenerationActive &&
                      (state.queryValue !== '' || state.updateQuery !== '' || state.pendingFile) ? (
                        <button
                          type="button"
                          ref={sendButtonRef}
                          disabled={
                            aiIsResponding ||
                            isSendBlockedByFile ||
                            RagOptions?.length === 0 ||
                            isSelectedModelAdminBlocked
                          }
                          onClick={handleSendQuery}
                          className="flex items-center justify-center w-9 h-9 bg-[#0A8BC7] hover:bg-[#247ba4] rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <StyledTooltip title="Send Message">
                            <img
                              src={SendButtonIcon}
                              alt="send"
                              className="w-4 h-4 brightness-0 invert"
                            />
                          </StyledTooltip>
                        </button>
                      ) : null}
                    </>
                  }
                >
                  <div
                    ref={contentEditableRef}
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    tabIndex={0}
                    aria-label="Ask AI a question or type '@' to mention an agent"
                    aria-multiline="true"
                    aria-disabled={aiIsResponding || RagOptions?.length === 0 || showLlmSetupBanner}
                    className="send-container w-full border-0 resize-none max-h-[54px] bg-transparent py-1 rounded-[10px] scroller text-black overflow-y-auto whitespace-pre-wrap break-words focus:outline-none min-w-0"
                    style={{
                      minHeight: '24px',
                      maxHeight: '154px',
                      ...(aiIsResponding || RagOptions?.length === 0 || showLlmSetupBanner
                        ? { opacity: 0.5, cursor: 'not-allowed' }
                        : {}),
                    }}
                    data-placeholder={aiIsResponding || RagOptions?.length === 0 ? '' : 'Ask Grigo'}
                    onInput={(e) => {
                      if (aiIsResponding || RagOptions?.length === 0 || showLlmSetupBanner) {
                        const element = e.currentTarget;
                        const previousText = state.queryValue || '';
                        if (getPlainText(element) !== previousText) {
                          const formatted = formatQueryWithMentions(previousText);
                          element.innerHTML = formatted || '<br>';
                        }
                        return;
                      }
                      handleContentEditableInput(e);
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={(e) => {
                      if (aiIsResponding || RagOptions?.length === 0 || showLlmSetupBanner) {
                        e.preventDefault();
                        return;
                      }
                      const pastedFiles = e.clipboardData.files;
                      if (pastedFiles?.length > 0) {
                        e.preventDefault();
                        const file = pastedFiles[0];
                        processAndSetPendingFile(file);
                        return;
                      }
                      e.preventDefault();
                      const text = e.clipboardData.getData('text/plain');
                      const selection = window.getSelection();
                      if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        range.deleteContents();
                        const textNode = document.createTextNode(text);
                        range.insertNode(textNode);
                        range.setStartAfter(textNode);
                        range.setEndAfter(textNode);
                        selection.removeAllRanges();
                        selection.addRange(range);
                      }
                      handleContentEditableInput(e as unknown as React.FormEvent<HTMLDivElement>);
                    }}
                  />
                </ChatComposerShell>
                {isSelectedModelAdminBlocked ? (
                  <p className="px-1 pt-1 text-xs text-zinc-600">{ADMIN_DISABLED_MODEL_MESSAGE}</p>
                ) : null}

                {showAgentMention && (
                  <div
                    ref={agentMentionRef}
                    className="absolute bottom-full left-0 mb-2 w-72 p-2 rounded-lg bg-white border border-zinc-200 z-50 max-h-80 overflow-hidden flex flex-col"
                    role="listbox"
                    aria-label="Agents matching mention"
                  >
                    <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-2 py-1.5 uppercase tracking-wider">
                      {mentionQuery.trim()
                        ? `Agents matching “${mentionQuery.trim()}”`
                        : 'Select an agent'}
                    </p>
                    <div className="space-y-0.5 overflow-y-auto flex-1 min-h-0 scrollbar-hide">
                      {(() => {
                        if (filteredAgents.length > 0) {
                          return filteredAgents.map((agent, index) => (
                            <StyledTooltip
                              key={agent.id}
                              title={
                                agent.provider_id !== activeProvider
                                  ? 'This agent is not available for selected provider'
                                  : ''
                              }
                            >
                              <button
                                type="button"
                                onClick={() => handleAgentSelect(agent)}
                                onMouseEnter={() => setSelectedMentionIndex(index)}
                                tabIndex={0}
                                className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors ${
                                  index === selectedMentionIndex &&
                                  'bg-[hsl(var(--button-teal))]/10 text-[hsl(var(--foreground))]'
                                } ${agent.provider_id !== activeProvider ? 'opacity-50 cursor-not-allowed' : 'hover:cursor-pointer hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'}`}
                                disabled={agent.provider_id !== activeProvider}
                                role="option"
                                aria-selected={index === selectedMentionIndex}
                              >
                                <span className="text-lg flex-shrink-0 flex items-center justify-center w-6 h-6">
                                  <img
                                    src={ChatAgentIcon}
                                    alt=""
                                    className="w-4 h-4"
                                    style={{
                                      filter:
                                        'invert(48%) sepia(79%) saturate(2476%) hue-rotate(166deg) brightness(86%) contrast(101%)',
                                    }}
                                  />
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{agent.agent_name}</p>
                                  {agent.description ? (
                                    <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                                      {agent.description}
                                    </p>
                                  ) : null}
                                </div>
                                {index === selectedMentionIndex ? (
                                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--button-teal))] flex-shrink-0" />
                                ) : null}
                              </button>
                            </StyledTooltip>
                          ));
                        }
                        if (agents.length > 0 && mentionQueryLower) {
                          return (
                            <div className="px-4 py-6 text-center">
                              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                                No agents match “{mentionQuery.trim()}”
                              </p>
                              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                                Try another name or clear the filter
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                              No agents available
                            </p>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                              Create a chat agent to mention one here
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {/* Placeholder styling */}
                <style>{`
            [contenteditable][data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
            [contenteditable][data-placeholder].empty-content:before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
            [contenteditable][data-placeholder].empty-content br {
              display: none;
            }
          `}</style>
              </div>

              <div className="relative shrink-0 order-1 self-center p-1.5" ref={tunePopupRef}>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 rounded-full text-zinc-500 hover:bg-zinc-100 text-xl leading-none"
                  aria-label="Add"
                  aria-expanded={showTunePopup}
                  disabled={showLlmSetupBanner}
                  onClick={() => setShowTunePopup(!showTunePopup)}
                >
                  +
                </button>
                <div
                  className={`thin-scroller absolute bottom-full left-0 mb-2 bg-white border border-zinc-200 rounded-2xl w-[300px] max-h-[52vh] z-30 invisible-scroller py-2 overflow-y-auto overflow-x-hidden ${
                    showTunePopup ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {permittedFile?.[0]?.data?.includes('file-upload') ? (
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 text-left disabled:opacity-50"
                      disabled={
                        aiIsResponding ||
                        isFileUploadInProgress ||
                        isPiiConfirmationOpen ||
                        RagOptions?.length === 0 ||
                        showLlmSetupBanner
                      }
                      onClick={() => {
                        setShowTunePopup(false);
                        handleUploadIcon();
                      }}
                    >
                      <img src={AttachIcon} alt="" className="w-4 h-4 opacity-70" />
                      Upload files
                    </button>
                  ) : null}
                  <div className="w-full flex items-center gap-2 px-4 py-2.5">
                    <Globe
                      size={16}
                      className={`shrink-0 opacity-70 ${
                        webSearchMode === 'off' ? 'text-zinc-700' : 'text-[#0A8BC7]'
                      }`}
                    />
                    <span
                      className={`flex-1 text-sm ${
                        webSearchMode === 'off' ? 'text-zinc-700' : 'text-[#0A8BC7]'
                      }`}
                    >
                      Web Search
                    </span>
                    <div
                      className="inline-flex items-center rounded-full bg-zinc-100 p-0.5"
                      role="group"
                      aria-label="Web Search mode"
                    >
                      {(['auto', 'on', 'off'] as const).map((mode) => {
                        const isActive = webSearchMode === mode;
                        return (
                          <button
                            key={mode}
                            type="button"
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium leading-none disabled:opacity-50 ${
                              isActive
                                ? 'bg-[#0A8BC7] text-white'
                                : 'text-zinc-600 hover:text-zinc-800'
                            }`}
                            aria-pressed={isActive}
                            disabled={state.newResponseLoader || RagOptions?.length === 0}
                            onClick={() => persistWebSearchMode(mode)}
                          >
                            {WEB_SEARCH_MODE_LABELS[mode]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-zinc-50 disabled:opacity-50 ${
                      codeInterpreterEnabled ? 'text-[#0A8BC7]' : 'text-zinc-700'
                    }`}
                    disabled={state.newResponseLoader || RagOptions?.length === 0}
                    onClick={() =>
                      handleChatInterpreterToggle({
                        target: { checked: !codeInterpreterEnabled },
                      } as InputChangeEvent)
                    }
                  >
                    <Code size={16} className="shrink-0 opacity-70" />
                    <span className="flex-1">Code Interpreter</span>
                  </button>
                  {showThinkingOption ? (
                    <button
                      type="button"
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-zinc-50 ${
                        state.thinkingEnabled ? 'text-[#0A8BC7]' : 'text-zinc-700'
                      }`}
                      disabled={showLlmSetupBanner}
                      onClick={() => handleThinkingToggle(!state.thinkingEnabled)}
                    >
                      <img src={BrainIcon} alt="" className="w-4 h-4 opacity-70" />
                      Thinking
                    </button>
                  ) : null}
                  {currentProviderData?.data?.file_id ? (
                    <button
                      type="button"
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-zinc-50 ${
                        currentProviderData?.data?.use_document ? 'text-[#0A8BC7]' : 'text-zinc-700'
                      }`}
                      onClick={handleUseDocument}
                    >
                      <img src={TextFile} alt="" className="w-4 h-4 opacity-70" />
                      {UseDocument}
                    </button>
                  ) : null}

                  {/* Chat Agents Section */}
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 text-left"
                      onClick={() => setExpandedAgents(!expandedAgents)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedAgents(!expandedAgents);
                        }
                      }}
                    >
                      <img src={ChatAgentIcon} alt="" className="w-4 h-4 opacity-70" />
                      <span className="flex-1">Agents</span>
                      <span className="text-xs text-zinc-400">{agents?.length ?? 0}</span>
                      {expandedAgents ? (
                        <ChevronDown size={16} className="text-zinc-400" />
                      ) : (
                        <ChevronRight size={16} className="text-zinc-400" />
                      )}
                    </button>
                    {expandedAgents && (
                      <div className="max-h-[180px] overflow-y-auto">
                        {agents?.length > 0 ? (
                          agents?.map((agent) => (
                            <div
                              key={agent.id}
                              className="px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors"
                              onClick={() =>
                                setSelectedAgent(selectedAgent === agent.id ? null : agent.id)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedAgent(selectedAgent === agent.id ? null : agent.id);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex flex-col flex-1">
                                  <span className="text-sm font-medium text-[#344054]">
                                    {agent.agent_name}
                                  </span>
                                  {agent.description && (
                                    <span className="text-xs text-gray-500 line-clamp-2">
                                      {agent.description}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                  {agent.user_id ===
                                    JSON.parse(localStorage.getItem('user_id') ?? '0') && (
                                    <>
                                      <button
                                        type="button"
                                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditAgent(agent.id);
                                        }}
                                        aria-label={`Edit ${agent.agent_name}`}
                                      >
                                        <img src={EditIcon} alt="Edit" className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          showDeleteConfirmation(agent.id, agent.agent_name);
                                        }}
                                        aria-label={`Delete ${agent.agent_name}`}
                                      >
                                        <img
                                          src={DeleteIcon}
                                          alt="Delete"
                                          className="w-4 h-4 text-red-500"
                                        />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            No chat agents found. Create one to get started.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {showMcpOptions ? (
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 text-left"
                        onClick={() => setExpandedMcp(expandedMcp === null ? 0 : null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedMcp(expandedMcp === null ? 0 : null);
                          }
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} className="opacity-70" />
                        <span className="flex-1">MCP Connectors</span>
                        <span className="text-xs text-zinc-400">{mcpData.length}</span>
                        {expandedMcp !== null ? (
                          <ChevronDown size={16} className="text-zinc-400" />
                        ) : (
                          <ChevronRight size={16} className="text-zinc-400" />
                        )}
                      </button>
                      {expandedMcp !== null && (
                        <div className="max-h-[180px] overflow-y-auto">
                          {mcpData.length > 0 ? (
                            mcpData.map((mcp) => (
                              <div
                                key={mcp.id}
                                className="px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors min-w-0"
                              >
                                <div className="flex items-center justify-between gap-2 min-w-0">
                                  <div className="flex items-start gap-2 flex-1 min-w-0">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedMcp(expandedMcp === mcp.id ? null : mcp.id)
                                      }
                                      className="w-4 h-4 shrink-0 flex items-center justify-center bg-transparent border-none p-0 focus:outline-none hover:bg-gray-200 rounded mt-0.5"
                                    >
                                      {expandedMcp === mcp.id ? (
                                        <ChevronDown size={14} className="text-gray-600" />
                                      ) : (
                                        <ChevronRight size={14} className="text-gray-600" />
                                      )}
                                    </button>
                                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                                      <span className="text-sm font-medium text-[#344054] truncate">
                                        {mcp.name}
                                      </span>
                                      <div className="flex flex-wrap items-center gap-1">
                                        <McpStatusBadge status={mcp.status} />
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                          {getMcpAuthTypeLabel(mcp.auth_type)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {renderMcpPrimaryAction(mcp)}
                                    <button
                                      type="button"
                                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showDeleteMcpConfirmation(mcp.id, mcp.name);
                                      }}
                                      aria-label={`Delete ${mcp.name}`}
                                    >
                                      <img
                                        src={DeleteIcon}
                                        alt="Delete"
                                        className="w-4 h-4 text-red-500"
                                      />
                                    </button>
                                  </div>
                                </div>
                                {expandedMcp === mcp.id && (
                                  <div className="ml-4 mt-2 pt-2 border-t border-gray-100 min-w-0">
                                    <RenderMcpExpandedContent
                                      mcp={mcp}
                                      mcpRefreshingId={mcpRefreshingId}
                                      panelMessage={mcpPanelMessages[mcp.id]}
                                      onRefreshTools={handleRefreshMcpTools}
                                      onUpdateTool={handleUpdateMcpTool}
                                      onReconnect={openMcpConnectModal}
                                    />
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-center text-sm text-gray-500">
                              No MCPs found. Add one to get started.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 text-left"
                    onClick={() => {
                      setShowTunePopup(false);
                      setShowChatAgentModal(true);
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                    Add new Agent
                  </button>
                  {showMcpOptions ? (
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 text-left"
                      onClick={openAddMcpModal}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                      Add MCP
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            {grandTotalTokens > 0 ? (
              <div className="flex justify-end px-2 pb-1 text-[11px] text-zinc-400">
                Tokens used: {grandTotalTokens}
              </div>
            ) : null}
          </div>
        </ChatThreadLayout>

        <input
          type="file"
          accept=".txt,.csv,.png,.jpg,.jpeg,.webp,.pdf,.xlsx,.doc,.docx"
          className="hidden"
          onChange={handleFileChange}
          ref={fileInputRef}
          disabled={
            aiIsResponding ||
            isFileUploadInProgress ||
            isPiiConfirmationOpen ||
            RagOptions?.length === 0
          }
        />
      </div>

      {logout && createPortal(<Logout />, document.body)}
      {showTuneModal &&
        createPortal(
          <TuneModal
            open={showTuneModal}
            reAuth={reAuthOpenModa}
            prefill={mcpPrefillConfig ?? undefined}
            onClose={closeTuneModal}
            onSave={closeTuneModal}
            webSearchEnabled={webSearchEnabled}
            onWebSearchToggle={handleWebSearchToggle}
            disabled={state.newResponseLoader || RagOptions?.length === 0}
          />,
          document.body,
        )}
      {showChatAgentModal &&
        createPortal(
          <ChatAgentModal
            open={showChatAgentModal}
            onClose={() => {
              setShowChatAgentModal(false);
              setEditingAgent(null);
            }}
            onSave={async () => {
              await fetchAgents();
            }}
            editMode={!!editingAgent}
            agentData={editingAgent || undefined}
            llmPermissions={llmPermissions}
          />,
          document.body,
        )}
      {deleteConfirmation.show &&
        createPortal(
          <ActionModal
            className="w-[400px]"
            open={deleteConfirmation.show}
            resetData={() => setDeleteConfirmation({ show: false, agentId: null, agentName: '' })}
            confirmButtonText="Delete"
            saveData={handleDeleteAgent}
          >
            <span className="font-semibold text-center pb-3">
              Are you sure you want to delete &quot;{deleteConfirmation.agentName}&quot; agent?
            </span>
          </ActionModal>,
          document.body,
        )}
      {deleteMcpConfirmation.show &&
        createPortal(
          <ActionModal
            className="w-[400px]"
            open={deleteMcpConfirmation.show}
            resetData={() => setDeleteMcpConfirmation({ show: false, mcpId: null, mcpName: '' })}
            confirmButtonText="Delete"
            saveData={handleDeleteMcp}
          >
            <span className="font-semibold text-center pb-3">
              Are you sure you want to delete &quot;{deleteMcpConfirmation.mcpName}&quot; MCP
              configuration?
            </span>
          </ActionModal>,
          document.body,
        )}
      {piiConfirmation.show &&
        createPortal(
          <PiiUploadConfirmationModal
            open={piiConfirmation.show}
            piiScan={piiConfirmation.piiScan}
            onConfirm={handlePiiUploadConfirm}
            onCancel={handlePiiUploadCancel}
          />,
          document.body,
        )}
    </>
  );
}

export default QueryContainer;
